<script setup>
import { reactive, onMounted, ref, computed, watch, nextTick } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useValidacao } from '@/composables/useValidacao'
import { useAuthStore } from '@/stores/auth'
import ComexPipelineStatus from '@/components/base/fluxo/ComexPipelineStatus.vue'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import SemAcessoModulo from '@/components/base/util/SemAcessoModulo.vue'
import api from '@/services/api'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { criarConfigBuscaOperacaoCompra } from '@/search-definitions/operacaoCompraSearch'
import { loteFabricacaoSearch } from '@/search-definitions/loteFabricacaoSearch'
import { templatePrecificacaoSearch } from '@/search-definitions/templatePrecificacaoSearch'
import { criarConfigBuscaLocalEstoque } from '@/search-definitions/localEstoqueSearch'
import PurchaseOrderSmartMode from './PurchaseOrderSmartMode.vue'
import DocumentoEntradaGrid from '@/components/Compras/documentoEntrada/DocumentoEntradaGrid.vue'
import ClausulasGrid from '@/components/clausulas/ClausulasGrid.vue'
import { CLAUSULAS_COM_APLIC } from '@/constants/CLAUSULAS_COM_APLIC'
import { CLAUSULAS_COM_VISIB } from '@/constants/CLAUSULAS_COM_VISIB'
import { FIN_TP_TAX_CONVERT } from '@/constants/FIN_TP_TAX_CONVERT'
import { ST } from '@/constants/ST'
import { PO_ST } from '@/constants/PO_ST'
import { MOE_FONTE_COTACAO } from '@/constants/MOE_FONTE_COTACAO'
import { FIN_ST_TAX_CONVERCAO } from '@/constants/FIN_ST_TAX_CONVERCAO'
import { ESTQ_TIPO_LOCAL_ESTOCAGEM } from '@/constants/ESTQ_TIPO_LOCAL_ESTOCAGEM'
import { filialSearch } from '@/search-definitions/filialSearch'
import { fornecedorSearch } from '@/search-definitions/fornecedorSearch'
import RelatorioPreviewModal from '@/components/relatorios/RelatorioPreviewModal.vue'
import WorkflowAprovacaoGrid from '@/components/base/workflow/aprovacoes/WorkflowAprovacaoGrid.vue'
import { WORK_APR_PROCESSO } from '@/constants/WORK_APR_PROCESSO'
import { formatarDataBR, formatarDataHoraBR } from '@/utils/useDataBR'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idPurchaseOrder: null,
      idContratoCompra: null,
      idFilial: null,
      idParceiroNegocioFornecedor: null,
      nomeReferencia: '',
      dataEmissao: new Date().toISOString().split('T')[0],
      idOperacao: null,
      lstStatusPurchaseOrder: PO_ST.EmDigitacao,
      numeroPurchaseOrder: '',
      numeroProformaFornecedor: '',
      previsaoProducao: '',
      previsaoEmbarqueETD: null,
      previsaoEmbarqueETA: null,
      observacoes: '',
      lstFormaPagamento: null,
      lstIncoterm: null,
      lstLocalIncoterm: null,
      lstMoeda: '',
      atributosPrecificacaoJson: null,
      percentualTolerancia: null,
      idTemplatePrecificacao: null,
      idLocalEstoquePrevisto: null,
      valorTotalOriginal: 0,
      valorTotalFrete: 0,
      valorTotalDesconto: 0,
      valorTotalNegociado: 0,
      valorTotalOriginalGerencial: null,
      valorTotalFreteGerencial: null,
      valorTotalDescontoGerencial: null,
      valorTotalNegociadoGerencial: null,
      lstMoedaGerencial: null,
      lstTipoTaxaConversao: null,
      dataAlvoBuscaCambio: null,
      dataReferenciaCambio: null,
      lstTaxaCambioStatus: null,
      lstFonteCambio: null,
      taxaCambioGerencialPadrao: null,
      lstMoedaPadrao: '',
      taxaCambioPadrao: null,
      taxaCambioGerencial: null,
      valorTotalOriginalPadrao: null,
      valorTotalFretePadrao: null,
      valorTotalDescontoPadrao: null,
      valorTotalNegociadoPadrao: null,
      // Aprovação
      idGrupoAcessoAprovador: null,
      idUsuarioAprovador: null,
      resumoAprovacao: '',
      observacaoReprovacao: '',
      // Cláusulas
      clausulasInicializadas: false
    })
  },
  contextoPai: {
    type: Object,
    default: () => ({})
  },
  drilldownMode: {
    type: Boolean,
    default: false
  },
  pipelineProps: {
    type: Object,
    default: () => ({})
  },
  funcao: {
    type: String,
    default: 'edicao',
    validator: (v) => ['edicao', 'adicao'].includes(v)
  },
  // prePreencher: null = modo espera (pergunta ao usuário),
  // true = pré-preenche dados do contrato,
  // false = adição sem pré-preenchimento,
  // undefined = fora do fluxo de contrato (PurchaseOrderAdministrar)
  prePreencher: {
    default: undefined
  },
  // modoContratoCompra: indica que o PurchaseOrderForm está sendo usado
  // dentro do pipeline de ContratoCompraCadastro (contrato de compra).
  // Quando true, exibe o grid de POs vinculadas ao contrato.
  // Acionado exclusivamente pelo ContratoCompraForm ao fazer drilldown-push.
  modoContratoCompra: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits([
  'itemAtualizado',
  'cancelar',
  'show-notification',
  'drilldown-pop',
  'pipeline-navegar',
  'drilldown-push',
  'update:funcao',
  'changesDetected'
])

// ============================================================
// WORKFLOW DE APROVAÇÃO (WorkflowAprovacaoGrid)
// ============================================================
const workflowGridRef = ref(null)
const processosAprovacao = [
  { chave: WORK_APR_PROCESSO.PurchaseOrder, label: 'Purchase Order', chavePermissao: 'APROVACAO_PO_APROVAR' }
]

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const purchaseOrdersContrato = ref([])
const isLoadingPurchaseOrders = ref(false)

const purchaseOrdersContratoOrdenadas = computed(() => {
  return [...purchaseOrdersContrato.value].sort((a, b) => {
    if (!a.dataEmissao) return 1
    if (!b.dataEmissao) return -1
    return new Date(a.dataEmissao) - new Date(b.dataEmissao)
  })
})
const listas = reactive({})
const mostrarAuditoria = ref(false)

// Promise que resolve quando as listas do sistema terminam de carregar
let resolveListasCarregadas = null
const listasCarregadasPromise = new Promise((resolve) => { resolveListasCarregadas = resolve })

const produtosPurchaseOrder = ref([])
const isLoadingProdutosPurchaseOrder = ref(false)
const produtosContratoPai = ref([]) // Itens definidos no contrato
const todosProdutosPurchaseOrdersDoContrato = ref([]) // Itens alocados em TODAS as purchase orders do contrato

const produtosComSaldoPendente = computed(() => {
  return produtosContratoPai.value.map(pc => {
    // Soma o que já foi alocado em unidade FORNECEDOR
    const alocadoForn = todosProdutosPurchaseOrdersDoContrato.value
      .filter(itemPO => itemPO.idContratoCompraProduto === pc.idContratoCompraProduto)
      .reduce((acc, itemPO) => acc + (Number(itemPO.quantidadeUnFornecedorNegociada) || 0), 0)

    // Soma o que já foi alocado em unidade BÁSICA
    const alocadoBase = todosProdutosPurchaseOrdersDoContrato.value
      .filter(itemPO => itemPO.idContratoCompraProduto === pc.idContratoCompraProduto)
      .reduce((acc, itemPO) => acc + (Number(itemPO.quantidadeUnBasicaNegociada) || 0), 0)

    const pendenteForn = Math.max(0, (Number(pc.quantidadeUnFornecedorNegociada) || 0) - alocadoForn)
    const pendenteBase = Math.max(0, (Number(pc.quantidadeUnBasicaNegociada) || 0) - alocadoBase)

    return {
      ...pc,
      qtdContratoForn: pc.quantidadeUnFornecedorNegociada,
      qtdPendenteForn: parseFloat(pendenteForn.toFixed(4)),
      qtdPendenteBase: parseFloat(pendenteBase.toFixed(4)),
      permiteConversaoVariavel: pc.permiteConversaoVariavel || false
    }
  })
})

// Computed property para calcular o valor total original da purchase order com base nos produtos
const valorTotalCalculado = computed(() => {
  if (!produtosPurchaseOrder.value || produtosPurchaseOrder.value.length === 0) return 0

  return produtosPurchaseOrder.value.reduce((total, prod) => {
    const quantidade = parseNumber(prod.quantidadeUnFornecedorNegociada) || 0
    const preco = parseNumber(prod.valorUnitarioOriginal) || 0
    return total + (quantidade * preco)
  }, 0)
})

const percentualPendenteDocs = computed(() => {
  const totalPO = valorTotalCalculado.value
  if (totalPO <= 0) return 0
  // SmartMode autônomo — não rastreamos mais totais consumidos localmente
  const totalConsumido = 0
  const percentualAlocado = Math.min(100, Math.round((totalConsumido / totalPO) * 100))
  return 100 - percentualAlocado
})

// ============================================================
// ACCORDION STATE (frames colapsáveis)
// IMPORTANTE: NUNCA declarar nomes de abas como chaves aqui
// (ex: operacao, comercial, pricing, cambio). O useValidacao
// trata qualquer chave do accordionState como frame/accordion
// e os nomes de abas como abas internas — a mesma string não
// pode representar os dois papéis, sob risco de quebrar a
// navegação automática para o campo com erro.
// ============================================================
const accordionState = reactive({
  dadosBasicos: true,
  abas: true,
  produtos: true,
  clausulas: false,
  documentosEntrada: true
})

// ============================================================
// TOGGLE DO HEADER DE ABAS — não expande ao clicar nas tabs
// ============================================================
const toggleAbasHeader = (event) => {
  // Se o clique foi em um botão de tab ou dentro de um nav-item,
  // expande o accordion (se colapsado) sem nunca colapsar
  if (event.target.closest('.nav-item, .nav-link, button[role="tab"]')) {
    accordionState.abas = true
    return
  }
  // Clique no restante do header: toggle normal
  accordionState.abas = !accordionState.abas
}

// ============================================================
// ABA ATIVA (tabs: operacao, comercial, pricing, cambio)
// ============================================================
const abaAtiva = ref('operacao')

// ============================================================
// FUNÇÃO DO COMPONENTE (Edição / Adição)
// ============================================================
const funcaoInterna = ref(props.funcao)

watch(() => props.funcao, (v) => {
  funcaoInterna.value = v
})

const alterarFuncao = (nova) => {
  funcaoInterna.value = nova
  emit('update:funcao', nova)
}

// ============================================================
// MODO DE ESPERA (prePreencher === null)
// ============================================================
const prePreencherConfirmado = ref(false)

// Inicializa prePreencherValor: null = modo espera, true = pré-preenche, false = adição vazia
// Se está no fluxo do contrato (idContratoEfetivo) e prePreencher não foi passado (undefined),
// entra em modo espera (null). Se veio do ContratoCompraForm com null, também entra em modo espera.
const prePreencherValor = ref(
  props.prePreencher === undefined && (props.contextoPai?.idContratoCompra || props.pipelineProps?.idContrato)
    ? null
    : props.prePreencher
)

// Modo espera: quando prePreencher é null e estamos no fluxo do contrato
// Não depende de funcaoInterna para ativar — o modo espera deve ficar ativo
// até que o usuário decida sobre o pré-preenchimento (clicando em "Adicionar PO").
const modoEspera = computed(() => {
  return prePreencherValor.value === null && !!idContratoEfetivo.value
})

// Modal de confirmação para pré-preenchimento
const prePreencherModalRef = ref(null)

const aplicarDadosContrato = (contrato) => {
  // Aplica os campos do contrato que existem na model ContratoCompra
  // (IdFilial, IdParceiroNegocioFornecedor, LstMoeda, PercentualTolerancia,
  //  AtributosPrecificacaoJson, NumeroContratoFornecedor).
  // Campos de comercial/câmbio/template NÃO existem no ContratoCompra
  // e permanecem para o usuário preencher na PO.
  item.value.idFilial = contrato.idFilial || null
  item.value.idParceiroNegocioFornecedor = contrato.idParceiroNegocioFornecedor || null
  item.value.lstMoeda = contrato.lstMoeda || ''
  item.value.percentualTolerancia = contrato.percentualTolerancia || null
  item.value.atributosPrecificacaoJson = contrato.atributosPrecificacaoJson || null

  // Sincroniza os campos de pricing a partir do JSON do contrato
  if (contrato.atributosPrecificacaoJson) {
    sincronizarJsonParaValores()
  }
}

const iniciarComPrePreencher = async () => {
  prePreencherValor.value = true
  prePreencherConfirmado.value = true
  // Baseline = modelo vazio
  initializeForm(props.modeloVazio)

  const idContrato = idContratoEfetivo.value
  if (idContrato) {
    item.value.idContratoCompra = idContrato
    // Busca os dados do contrato no BANCO (fonte da verdade)
    try {
      const res = await api.get(`/ContratosCompra/${idContrato}`)
      const contrato = res.data
      aplicarDadosContrato(contrato)
    } catch (e) {
      console.warn('[iniciarComPrePreencher] Erro ao buscar contrato do banco, usando fallback:', e)
      emit('show-notification', {
        message: 'Não foi possível carregar os dados do contrato do banco. O formulário será iniciado vazio — preencha os campos manualmente.',
        type: 'warning'
      })
      // Fallback: usa contextoPai se disponível (apenas campos que existem no ContratoCompra)
      if (props.contextoPai) {
        const cp = props.contextoPai
        item.value.idFilial = cp.idFilial || null
        item.value.idParceiroNegocioFornecedor = cp.idParceiroNegocioFornecedor || null
        item.value.lstMoeda = cp.lstMoeda || ''
        item.value.percentualTolerancia = cp.percentualTolerancia || null
        item.value.atributosPrecificacaoJson = cp.atributosPrecificacaoJson || null
      }
    }
  }
  // Força hasChanges = true pois o watcher do Vue é assíncrono
  // e pode não ter disparado ainda quando o usuário tentar sair
  hasChanges.value = true
  alterarFuncao('adicao')
  purchaseOrdersGridAberto.value = false
}

// eslint-disable-next-line no-unused-vars -- usada no template @confirmed
const onPrePreencherConfirm = (confirmed) => {
  if (confirmed) {
    // iniciarComPrePreencher é async; a Promise roda em background.
    // O modal fecha e o formulário é exibido — os dados do contrato
    // são carregados assincronamente e aplicados quando a resposta chegar.
    iniciarComPrePreencher()
  } else {
    iniciarSemPrePreencher()
  }
}

const iniciarSemPrePreencher = () => {
  prePreencherValor.value = false
  prePreencherConfirmado.value = true
  // Reseta para o estado vazio
  initializeForm(props.modeloVazio)
  if (idContratoEfetivo.value) {
    item.value.idContratoCompra = idContratoEfetivo.value
  }
  alterarFuncao('adicao')
  purchaseOrdersGridAberto.value = false
}

// ============================================================
// COLLAPSE DO GRID DE PURCHASE ORDERS
// ============================================================
const purchaseOrdersGridAberto = ref(true)

watch(() => funcaoInterna.value, (novaFuncao) => {
  if (novaFuncao === 'adicao' && prePreencherConfirmado.value) {
    purchaseOrdersGridAberto.value = false
  }
})

const togglePurchaseOrdersGrid = () => {
  purchaseOrdersGridAberto.value = !purchaseOrdersGridAberto.value
}

// ============================================================
// ESTADO PARA DOCUMENTOS DE ENTRADA / FATURAS
// ============================================================
const documentosEntrada = ref([])
const documentoEntradaGridRef = ref(null)

// ============================================================
// TOTAIS CONSOLIDADOS POR PURCHASE ORDER (considera TODOS os documentos)
const {
  item,
  isLoading,
  isEditing,
  hasChanges,
  errors,
  focarPrimeiroErro,
  submitForm,
  initializeForm
} = useFormulario(props, emit)

// Padrão de validação híbrida: processa erros estruturados do backend
// (ValidationErrorResponse) destacando campos, abrindo accordions/tabs e focando no erro
const { processarErrosBackend: processarErrosValidacaoPO } = useValidacao({
  formErrors: errors,
  accordionState,
  activeTab: abaAtiva,
  fieldTabMap: {
    idFilial: 'dadosBasicos',
    idParceiroNegocioFornecedor: 'dadosBasicos',
    idOperacao: 'dadosBasicos',
    nomeReferencia: 'dadosBasicos',
    dataEmissao: 'dadosBasicos',
    previsaoProducao: 'dadosBasicos',
    previsaoEmbarqueETD: 'dadosBasicos',
    previsaoEmbarqueETA: 'dadosBasicos',
    lstMoeda: 'comercial',
    lstIncoterm: 'comercial',
    lstLocalIncoterm: 'comercial',
    lstFormaPagamento: 'comercial',
    idLocalEstoquePrevisto: 'comercial',
    atributosPrecificacaoJson: 'pricing',
    produtos: 'produtos',
    condicoesPagamento: 'comercial',
    percentualTolerancia: 'comercial',
    idGrupoAcessoAprovador: 'dadosBasicos',
    resumoAprovacao: 'dadosBasicos'
  }
})

const isPurchaseOrderSaved = ref(false)

// ============================================================
// ESTADOS DE BLOQUEIO DOS FRAMES
// ============================================================
// 3 estados:
//   1. 'tudoBloqueado'  — sem seleção (modoEspera), tudo bloqueado exceto grid POs
//   2. 'apenasBasicos'  — adição nova PO, só Dados Básicos liberado
//   3. 'tudoLiberado'   — edição de PO existente, tudo liberado
const estadoBloqueio = computed(() => {
  if (modoEspera.value) return 'tudoBloqueado'
  if (funcaoInterna.value === 'adicao') {
    return isPurchaseOrderSaved.value ? 'tudoLiberado' : 'apenasBasicos'
  }
  if (funcaoInterna.value === 'edicao') return 'tudoLiberado'
  return 'tudoBloqueado'
})

const abasBloqueadas = computed(() => {
  return estadoBloqueio.value !== 'tudoLiberado'
})

const frameBasicosBloqueado = computed(() => {
  return estadoBloqueio.value === 'tudoBloqueado'
})

const framesSecundariosBloqueados = computed(() => {
  return estadoBloqueio.value !== 'tudoLiberado'
})
const initialLoadDone = ref(false)

const confirmationModalRef = ref(null)
const pendingAction = ref(null)

// Refs para os modais de confirmação de transição de status
const confirmCancelarRef = ref(null)
const confirmEmitirRef = ref(null)
const confirmSolicitarAprovacaoRef = ref(null)
const confirmEncerrarRef = ref(null)
const confirmEncerrarParcialmenteRef = ref(null)
const confirmRetornarPendenteRef = ref(null)

// ============================================================
// LABELS DINÂMICAS DE TAXA (Fase 4.4)
// ============================================================
const obterSiglaMoeda = (codigoMoeda) => {
  if (!codigoMoeda) return '-'
  const idStr = String(codigoMoeda)
  const itemLista = (listas.LstMoeda || []).find(m =>
    String(m.idListaClienteItem) === idStr || String(m.idListaClienteItem) === idStr
  )
  const texto = itemLista ? itemLista.textoLista : codigoMoeda
  const match = String(texto).match(/^[A-Z]{3}/)
  return match ? match[0] : codigoMoeda
}

const labelTaxaPadrao = computed(() => {
  const siglaPO = obterSiglaMoeda(item.value.lstMoeda)
  const siglaPadrao = obterSiglaMoeda(item.value.lstMoedaPadrao)
  return `Taxa ${siglaPO}/${siglaPadrao}`
})

const labelTaxaGerencialPadrao = computed(() => {
  const siglaGerencial = obterSiglaMoeda(item.value.lstMoedaGerencial)
  const siglaPadrao = obterSiglaMoeda(item.value.lstMoedaPadrao)
  return `Taxa ${siglaGerencial}/${siglaPadrao}`
})

const labelTaxaCambioGerencial = computed(() => {
  const siglaPO = obterSiglaMoeda(item.value.lstMoeda)
  const siglaGerencial = obterSiglaMoeda(item.value.lstMoedaGerencial)
  return `Taxa ${siglaPO}/${siglaGerencial}`
})

// ============================================================
// CÂMBIO — Resolução Automática (Fase 6)
// ============================================================
const isResolvendoTaxas = ref(false)

const resolverTaxasAutomaticamente = async () => {
  if (!item.value.idPurchaseOrder || isResolvendoTaxas.value) return
  isResolvendoTaxas.value = true
  try {
    const res = await api.post(`/PurchaseOrders/${item.value.idPurchaseOrder}/resolver-taxas`)
    Object.assign(item.value, res.data)
    // Gap 4: limpar preview para forçar fallback aos valores persistidos
    dadosCambioPreview.value = null
    emit('show-notification', { message: 'Taxas de câmbio resolvidas automaticamente.', type: 'success' })
  } catch (e) {
    const errorMsg = e.response?.data?.message || 'Erro ao resolver taxas de câmbio.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  } finally {
    isResolvendoTaxas.value = false
  }
}

// ============================================================
// CÂMBIO — Modal de Cotação Manual (Fase 4)
// ============================================================
const isModalCotacaoManualOpen = ref(false)

const cotacaoManualData = reactive({
  taxaCambioPadrao: null,
  taxaCambioGerencialPadrao: null,
  taxaCambioGerencial: null
})

watch(
  () => [cotacaoManualData.taxaCambioPadrao, cotacaoManualData.taxaCambioGerencialPadrao],
  ([taxaPadrao, taxaGerencialPadrao]) => {
    if (taxaPadrao && taxaGerencialPadrao && Number(taxaGerencialPadrao) !== 0) {
      cotacaoManualData.taxaCambioGerencial = Math.round(
        (Number(taxaPadrao) / Number(taxaGerencialPadrao)) * 1e6
      ) / 1e6
    }
  }
)

const abrirModalCotacaoManual = () => {
  cotacaoManualData.taxaCambioPadrao = item.value.taxaCambioPadrao
  cotacaoManualData.taxaCambioGerencialPadrao = item.value.taxaCambioGerencialPadrao
  cotacaoManualData.taxaCambioGerencial = item.value.taxaCambioGerencial
  isModalCotacaoManualOpen.value = true
}

const salvarCotacaoManual = async () => {
  item.value.taxaCambioPadrao = cotacaoManualData.taxaCambioPadrao
  item.value.taxaCambioGerencialPadrao = cotacaoManualData.taxaCambioGerencialPadrao
  item.value.taxaCambioGerencial = cotacaoManualData.taxaCambioGerencial
  item.value.lstFonteCambio = MOE_FONTE_COTACAO.Manual
  item.value.lstTaxaCambioStatus = FIN_ST_TAX_CONVERCAO.Definitiva

  // Gap 1 corrigido: usar MULTIPLICAÇÃO (consistente com ConversaoMonetariaService)
  const taxaPadrao = Number(item.value.taxaCambioPadrao) || 0
  const taxaGerencial = Number(item.value.taxaCambioGerencial) || 0

  if (taxaPadrao > 0) {
    item.value.valorTotalOriginalPadrao = Math.round(item.value.valorTotalOriginal * taxaPadrao * 100) / 100
    item.value.valorTotalFretePadrao = Math.round(item.value.valorTotalFrete * taxaPadrao * 100) / 100
    item.value.valorTotalDescontoPadrao = Math.round(item.value.valorTotalDesconto * taxaPadrao * 100) / 100
    item.value.valorTotalNegociadoPadrao = Math.round(item.value.valorTotalNegociado * taxaPadrao * 100) / 100
  }
  if (taxaGerencial > 0) {
    item.value.valorTotalOriginalGerencial = Math.round(item.value.valorTotalOriginal * taxaGerencial * 100) / 100
    item.value.valorTotalFreteGerencial = Math.round(item.value.valorTotalFrete * taxaGerencial * 100) / 100
    item.value.valorTotalDescontoGerencial = Math.round(item.value.valorTotalDesconto * taxaGerencial * 100) / 100
    item.value.valorTotalNegociadoGerencial = Math.round(item.value.valorTotalNegociado * taxaGerencial * 100) / 100
  }

  if (item.value.idPurchaseOrder) {
    try {
      const payload = { ...item.value }
      // Remove idContratoCompra = 0 para evitar FK violation
      if (!payload.idContratoCompra) delete payload.idContratoCompra
      await api.put(`/PurchaseOrders/${item.value.idPurchaseOrder}`, payload)
      // Recarrega os dados atualizados do backend
      const res = await api.get(`/PurchaseOrders/${item.value.idPurchaseOrder}`)
      Object.assign(item.value, res.data)
      dadosCambioPreview.value = null
      emit('show-notification', { message: 'Taxas de câmbio atualizadas com sucesso.', type: 'success' })
    } catch (error) {
      emit('show-notification', { message: 'Erro ao salvar taxas de câmbio.', type: 'error' })
    }
  }

  isModalCotacaoManualOpen.value = false
}

// ============================================================
// CÂMBIO — Preview (Fase 6.2)
// ============================================================
const dadosCambioPreview = ref(null)

const onAcaoDocumentoCompleta = async ({ tipoAcao: _tipoAcao, documento: _documento }) => {
  // O SmartMode agora é autônomo — não precisa mais recarregar totais consumidos aqui
}

const entityInfoPurchaseOrder = computed(() => ({
  id: item.value.idPurchaseOrder,
  name: item.value.nomeReferencia || 'Nova Purchase Order',
  nameLabel: 'Purchase Order'
}))

const podeEditar = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')) return true
  return authStore.getPermsDoModulo('IMPORTACAO').some(p =>
    p === 'COMPLETO' || (p.includes('PURCHASE_ORDERS') && p.includes('COMPLETO'))
  )
})

const temModuloImportacao = computed(() => {
  return authStore.temModulo('IMPORTACAO')
})

// Verifica especificamente se o usuário tem permissão de PURCHASE_ORDERS com COMPLETO
const temPurchaseOrdersCompleto = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.pode('PURCHASE_ORDERS_COMPLETO')) return true
  // Fallback: busca permissões do módulo IMPORTACAO que contenham "PURCHASE_ORDERS" e "COMPLETO"
  return authStore.getPermsDoModulo('IMPORTACAO').some(p =>
    p === 'COMPLETO' || (p.includes('PURCHASE_ORDERS') && p.includes('COMPLETO'))
  )
})

// Se tem VISUALIZACAO, pode navegar entre purchase orders para visualizar
const temVisualizacao = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.pode('CONTRATOS_COMPRA_VISUALIZACAO')) return true
  if (authStore.pode('PURCHASE_ORDERS_VISUALIZACAO')) return true
  return authStore.getPermsDoModulo('IMPORTACAO').some(p =>
    p === 'COMPLETO' || p.includes('VISUALIZACAO')
  )
})

const temDocumentoEntrada = computed(() => {
  return authStore.temModulo('DOCUMENTO_ENTRADA')
})

const idContratoEfetivo = computed(() => {
  // Fonte da verdade: o item carregado do banco (GetById já retorna IdContratoCompra).
  // Fallback para contextoPai/pipelineProps apenas em criação (item ainda sem idContratoCompra).
  return item.value.idContratoCompra || props.contextoPai?.idContratoCompra || props.pipelineProps?.idContrato
})

const modoAlocacaoSmartMode = computed(() => {
  return item.value.idContratoCompra ? 'saldo' : 'livre'
})

const carregarPurchaseOrders = async () => {
  // idContratoEfetivo já prioriza item.value.idContratoCompra (fonte da verdade = banco)
  // com fallback para contextoPai/pipelineProps
  const idContrato = idContratoEfetivo.value
  if (!idContrato) return

  isLoadingPurchaseOrders.value = true
  try {
    const res = await api.get('/PurchaseOrders', {
      params: { idContratoCompra: idContrato }
    })
    purchaseOrdersContrato.value = res.data
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar lista de purchase orders.', type: 'error' })
  } finally {
    isLoadingPurchaseOrders.value = false
  }
}

const carregarProdutosContrato = async () => {
  // idContratoEfetivo já prioriza item.value.idContratoCompra (fonte da verdade = banco)
  // com fallback para contextoPai/pipelineProps
  const idContrato = idContratoEfetivo.value
  if (!idContrato) return

  try {
    const [resProdutos, resTodosItensPOs] = await Promise.all([
      api.get('/ContratoCompraProdutos', { params: { idContratoCompra: idContrato } }),
      api.get('/PurchaseOrderProdutos', { params: { idContratoCompra: idContrato } })
    ])
    produtosContratoPai.value = resProdutos.data
    todosProdutosPurchaseOrdersDoContrato.value = resTodosItensPOs.data
  } catch (e) {
    console.error('Erro ao carregar dados do contrato pai para saldos:', e)
  }
}

const carregarProdutosPurchaseOrder = async (background = false) => {
  if (!item.value.idPurchaseOrder) {
    produtosPurchaseOrder.value = []
    return
  }
  if (!background) isLoadingProdutosPurchaseOrder.value = true
  try {
    const res = await api.get(`/PurchaseOrderProdutos/purchase-order/${item.value.idPurchaseOrder}`)
    produtosPurchaseOrder.value = res.data
  } catch (e) {
    // Se o endpoint retornar 404 (rota não encontrada), usa os produtos já carregados do GetById
    if (e.response?.status === 404) {
      console.warn('[carregarProdutosPurchaseOrder] Endpoint não encontrado (404), usando produtos do cache.')
      if (item.value.produtosSolicitados && item.value.produtosSolicitados.length > 0) {
        produtosPurchaseOrder.value = item.value.produtosSolicitados
      }
    } else {
      emit('show-notification', { message: 'Erro ao carregar produtos da purchase order.', type: 'error' })
    }
  } finally {
    if (!background) isLoadingProdutosPurchaseOrder.value = false
  }
}

const parseNumber = (val) => Number(String(val).replace(',', '.')) || 0

// ============================================================
// HANDLERS DO SMART MODE (DRAG & DROP)
// ============================================================
const handleAlocarProdutoSmartMode = async (dados) => {
  if (!item.value.idPurchaseOrder) {
    emit('show-notification', { message: 'Salve os dados básicos da purchase order antes de adicionar produtos.', type: 'warning' })
    return
  }

  const isEditing = !!dados.idPurchaseOrderProduto

  // Validação de saldo apenas em modo saldo (PO com contrato)
  if (modoAlocacaoSmartMode.value === 'saldo') {
    // Na edição, busca a quantidade alocada atual para calcular saldo correto
    if (isEditing) {
      const produtoAtual = produtosPurchaseOrder.value.find(p => p.idPurchaseOrderProduto === dados.idPurchaseOrderProduto)
      const qtdAlocadaForn = Number(produtoAtual?.quantidadeUnFornecedorNegociada) || 0

      const qtdDigitada = Number(dados.quantidadeUnFornecedorNegociada) || 0
      const saldoDisponivel = produtosComSaldoPendente.value.find(sp => sp.idContratoCompraProduto === dados.idContratoCompraProduto)
      const saldoForn = Number(saldoDisponivel?.qtdPendenteForn) || 0

      // Saldo real considerando que o produto será removido e readicionado
      if (qtdDigitada > (saldoForn + qtdAlocadaForn)) {
        emit('show-notification', {
          message: `Saldo insuficiente no contrato. Quantidade máxima permitida: ${(saldoForn + qtdAlocadaForn).toFixed(4)}`,
          type: 'error'
        })
        return
      }
    } else {
      // Validação de saldo para nova alocação
      try {
        await carregarProdutosContrato()

        const saldoAtual = produtosComSaldoPendente.value.find(sp => sp.idContratoCompraProduto === dados.idContratoCompraProduto)
        if (saldoAtual) {
          const qtdDigitada = Number(dados.quantidadeUnFornecedorNegociada) || 0
          if (qtdDigitada > (Number(saldoAtual.qtdPendenteForn) || 0)) {
            emit('show-notification', {
              message: `Saldo insuficiente no contrato. Saldo disponível: ${Number(saldoAtual.qtdPendenteForn).toFixed(4)} ${getTextoUnidade(dados.lstUnidadeMedidaFornecedor)}`,
              type: 'error'
            })
            return
          }
        }
      } catch (e) {
        console.error('Erro ao validar saldos antes de salvar:', e)
      }
    }
  }

  const payload = {
    idPurchaseOrder: item.value.idPurchaseOrder,
    idContratoCompraProduto: dados.idContratoCompraProduto || null,
    idProduto: dados.idProduto,
    nomeProduto: dados.nomeProduto,
    sku: dados.sku,
    quantidadeUnFornecedorNegociada: Number(dados.quantidadeUnFornecedorNegociada) || 0,
    quantidadeUnBasicaNegociada: Number(dados.quantidadeUnBasicaNegociada) || 0,
    fatorConversao: Number(dados.fatorConversao) || 0,
    lstUnidadeMedidaFornecedor: dados.lstUnidadeMedidaFornecedor,
    lstUnidadeMedidaBasica: dados.lstUnidadeMedidaBasica,
    valorUnitarioOriginal: Number(dados.valorUnitarioOriginal) || 0,
    valorTotalOriginal: Number(dados.valorTotalOriginal) || 0,
    valorUnitarioDesconto: Number(dados.valorUnitarioDesconto) || 0,
    valorTotalNegociado: Number(dados.valorTotalNegociado) || 0,
    valorTotalDesconto: Number(dados.valorTotalDesconto) || 0,
    lstMotivoDesconto: dados.lstMotivoDesconto || null,
    percentualTolerancia: dados.percentualTolerancia != null ? Number(dados.percentualTolerancia) : null,
    observacao: dados.observacao || '',
    lstStatusProdutoPo: dados.lstStatusProdutoPo || null,
    lstPaisOrigem: dados.lstPaisOrigem || null
  }

  try {
    if (isEditing) {
      await api.put(`/PurchaseOrderProdutos/${dados.idPurchaseOrderProduto}`, { ...payload, idPurchaseOrderProduto: dados.idPurchaseOrderProduto })
      emit('show-notification', { message: 'Produto atualizado na purchase order com sucesso.', type: 'success' })
    } else {
      await api.post('/PurchaseOrderProdutos', payload)
      emit('show-notification', { message: 'Produto alocado na purchase order com sucesso.', type: 'success' })
    }
    await carregarProdutosPurchaseOrder(true)
    await carregarProdutosContrato()
    await carregarCabecalhoPurchaseOrder()
  } catch (e) {
    const errorMsg = e.response?.data?.message || (isEditing ? 'Erro ao atualizar produto na purchase order.' : 'Erro ao alocar produto na purchase order.')
    emit('show-notification', { message: errorMsg, type: 'error' })
    await carregarProdutosContrato()
  }
}

const handleRemoverProdutoSmartMode = async ({ idPurchaseOrderProduto }) => {
  if (!idPurchaseOrderProduto) return

  try {
    await api.delete(`/PurchaseOrderProdutos/${idPurchaseOrderProduto}`)
    emit('show-notification', { message: 'Produto removido da purchase order.', type: 'success' })
    await carregarProdutosPurchaseOrder(true)
    await carregarProdutosContrato()
    await carregarCabecalhoPurchaseOrder()
  } catch (e) {
    emit('show-notification', { message: 'Erro ao remover produto.', type: 'error' })
  }
}

const handleCancelarProdutoSmartMode = async ({ idPurchaseOrderProduto }) => {
  if (!idPurchaseOrderProduto) return

  try {
    await api.post(`/PurchaseOrderProdutos/${idPurchaseOrderProduto}/cancelar`)
    emit('show-notification', { message: 'Produto cancelado na purchase order.', type: 'success' })
    await carregarProdutosPurchaseOrder(true)
    await carregarProdutosContrato()
    await carregarCabecalhoPurchaseOrder()
  } catch (e) {
    const errorMsg = e.response?.data?.message || 'Erro ao cancelar produto.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  }
}

// ============================================================
// HELPER: Recarrega o cabeçalho da Purchase Order do backend
// para refletir os totais recalculados e persistidos (Original, Desconto, Negociado).
// O recalculo é feito no backend (PurchaseOrderProdutosController.RecalcularTotaisPurchaseOrder)
// após cada Create/Update/Delete de produto.
// ============================================================
const carregarCabecalhoPurchaseOrder = async () => {
  if (!item.value.idPurchaseOrder) return

  try {
    const res = await api.get(`/PurchaseOrders/${item.value.idPurchaseOrder}`)
    const dados = res.data

    // Atualiza apenas os campos de totais do formulário
    item.value.valorTotalOriginal = dados.valorTotalOriginal ?? 0
    item.value.valorTotalDesconto = dados.valorTotalDesconto ?? 0
    item.value.valorTotalNegociado = dados.valorTotalNegociado ?? 0
    item.value.valorTotalFrete = dados.valorTotalFrete ?? 0

    // Atualiza o grid de purchase orders do contrato
    const idx = purchaseOrdersContrato.value.findIndex(c => c.idPurchaseOrder === item.value.idPurchaseOrder)
    if (idx >= 0) {
      purchaseOrdersContrato.value[idx].valorTotalOriginal = dados.valorTotalOriginal ?? 0
      purchaseOrdersContrato.value[idx].valorTotalDesconto = dados.valorTotalDesconto ?? 0
      purchaseOrdersContrato.value[idx].valorTotalNegociado = dados.valorTotalNegociado ?? 0
    }
  } catch (e) {
    console.error('[carregarCabecalhoPurchaseOrder] Erro ao recarregar cabeçalho da PO:', e)
  }
}

const configBuscaLoteFabricacao = computed(() => ({
  ...loteFabricacaoSearch,
  titulo: 'Buscar Lote de Fabricação',
  filtrosFixos: {}
}))

const configBuscaFilial = computed(() => ({
  ...filialSearch,
  titulo: 'Buscar Filial',
  filtrosFixos: {
    idEmpresa: authStore.empresaAtiva
  }
}))

const configBuscaFornecedor = computed(() => ({
  ...fornecedorSearch,
  titulo: 'Buscar Fornecedor',
  filtrosFixos: {
    idEmpresa: authStore.empresaAtiva
  }
}))

const configBuscaTemplate = computed(() => ({
  ...templatePrecificacaoSearch,
  filtrosFixos: {
    lstStatus: ST.Ativo
  }
}))

const configBuscaLocalEstoque = computed(() => {
  const base = criarConfigBuscaLocalEstoque(authStore.organizacaoAtiva, item.value.idFilial, [ESTQ_TIPO_LOCAL_ESTOCAGEM.PreEmbarque])
  return {
    ...base,
    titulo: 'Buscar Local de Estoque',
    filtrosFixos: {
      ...(base.filtrosFixos || {}),
      idEmpresa: authStore.empresaAtiva
    }
  }
})

const getTextoUnidade = (id) => {
  const lista = listas.LstUnidadeMedidaFornecedor || listas.LstUnidadeMedidaBase || listas.LstUnidadeMedidaBasica || []
  const un = lista.find(u => u.idListaClienteItem === id)
  return un ? un.textoLista : (id || '-')
}

const carregarListas = async () => {
  try {
    // Carrega PurchaseOrder primeiro para que suas listas prevaleçam
    // sobre outras tabelas que usam o mesmo nome de campo (ex: DocumentoEntrada
    // mapeia LstFormaPagamento → FIN_FORMAS_PG, PurchaseOrder mapeia → FIN_FORMAS_PG_INTER)
    const resultPo = await metadataStore.carregarListasParaTabelas(['PurchaseOrder'])
    Object.assign(listas, resultPo)

    // Depois carrega as demais tabelas (listas já definidas não serão sobrescritas)
    const result = await metadataStore.carregarListasParaTabelas([
      'PurchaseOrderCondicaoPg',
      'PurchaseOrderProduto',
      'ContratoCompraProduto',
      'Produto',
      'Embarque',
      'DocumentoEntrada'
    ])
    Object.assign(listas, result)
  } catch (e) {
    console.error('Erro ao carregar listas auxiliares via metadados:', e)
    emit('show-notification', { message: 'Erro ao carregar listas auxiliares via metadados.', type: 'error' })
  } finally {
    if (resolveListasCarregadas) {
      resolveListasCarregadas()
      resolveListasCarregadas = null
    }
  }
}

const getTextoLista = (lista, id) => {
  if (!lista) return id || '-'
  const found = lista.find(l => l.idListaClienteItem === id || l.idListaSistemaItem === id)
  return found ? found.textoLista : (id || '-')
}

const getTextoMoeda = () => {
  if (!listas.LstMoeda) return item.value.lstMoeda || 'MOEDA'
  if (!item.value.lstMoeda) return 'MOEDA'
  const moeda = listas.LstMoeda.find(m => m.idListaClienteItem === item.value.lstMoeda)
  return moeda ? moeda.textoLista : item.value.lstMoeda
}

const obterCorStatus = (textoStatus) => {
  const s = String(textoStatus || '').toUpperCase()
  if (s.includes('CANCELADO') || s.includes('INATIVO') || s.includes('REPROVADO')) return 'badge-ghost-danger'
  if (s.includes('ABERTO') || s.includes('PENDENTE') || s.includes('EM DIGITAÇÃO') || s.includes('ST1')) return 'badge-ghost-warning'
  if (s.includes('ATIVO') || s.includes('APROVADO') || s.includes('CONCLUÍDO') || s.includes('FINALIZADO')) return 'badge-ghost-success'
  if (s.includes('EM ANÁLISE') || s.includes('ENVIADO')) return 'badge-ghost-info'
  if (s.includes('PROCESSANDO')) return 'badge-ghost-primary'
  return 'badge-ghost-secondary'
}

const formatarDecimal = (valor) => {
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor || 0)
}

const formatarMoeda = (valor) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0)
}

// ============================================================
// LÓGICA DE PRICING (Atributos Dinâmicos)
// ============================================================
const templateTemporario = ref(null)
const pricingFields = ref([])
const valoresPricing = reactive({})

const sincronizarJsonParaValores = () => {
  try {
    const data = item.value.atributosPrecificacaoJson ? JSON.parse(item.value.atributosPrecificacaoJson) : {}

    Object.keys(data).forEach(key => {
      if (key !== '_templateInfo') {
        valoresPricing[key] = data[key]
      }
    })

    if (data._templateInfo) {
      pricingFields.value = data._templateInfo.atributos || []
    }
  } catch (e) {
    console.error('Erro ao sincronizar JSON de pricing:', e)
  }
}

watch(valoresPricing, (novosValores) => {
  const payload = {
    ...novosValores,
    _templateInfo: {
      atributos: pricingFields.value
    }
  }
  item.value.atributosPrecificacaoJson = JSON.stringify(payload)
}, { deep: true })

// Sincroniza o valorTotalNegociado com a soma dos breakdowns
watch(
  () => [
    item.value.valorTotalOriginal,
    item.value.valorTotalFrete,
    item.value.valorTotalDesconto
  ],
  ([original, frete, desconto]) => {
    const orig = original ?? 0
    const fret = frete ?? 0
    const desc = desconto ?? 0
    item.value.valorTotalNegociado = orig + fret - desc
  },
  { deep: true }
)

// Watcher para detectar quando o template é selecionado ou limpo via CampoBusca
// Quando o template é trocado, SEMPRE carrega o novo template.
// O JSON salvo só é usado na inicialização (oldId é undefined/null).
watch(() => item.value.idTemplatePrecificacao, (newId, oldId) => {
  if (newId && newId !== oldId) {
    // Template foi trocado — carrega o novo template sempre
    carregarTemplate(newId)
  } else if (newId && !oldId) {
    // Inicialização: usa JSON salvo se existir, senão carrega da API
    if (item.value.atributosPrecificacaoJson) {
      sincronizarJsonParaValores()
    } else {
      carregarTemplate(newId)
    }
  } else if (!newId && oldId) {
    // Template limpo — remove atributos
    Object.keys(valoresPricing).forEach(key => delete valoresPricing[key])
    pricingFields.value = []
    item.value.atributosPrecificacaoJson = null
  }
})

const carregarTemplate = async (idTemplate) => {
  try {
    const res = await api.get(`/TemplatesPrecificacao/${idTemplate}`)
    const template = res.data
    aplicarTemplate(template)
  } catch (e) {
    console.error('Erro ao carregar template de precificação:', e)
    emit('show-notification', { message: 'Erro ao carregar template de precificação.', type: 'error' })
  }
}

const onTemplateSelecionado = (template) => {
  // Chamado quando o usuário seleciona um template no modal de busca do CampoBusca
  // O CampoBusca já atualizou item.idTemplatePrecificacao via v-model
  // O watcher acima irá disparar e chamar carregarTemplate
  // Mas como já temos o objeto completo, podemos aplicar diretamente
  templateTemporario.value = template
  const temValores = Object.values(valoresPricing).some(v => v !== null && v !== '' && v !== 0)

  if (temValores) {
    if (modalConfirmacaoTrocaRef.value) {
      modalConfirmacaoTrocaRef.value.show()
    }
  } else {
    aplicarTemplate(template)
  }
}

const aplicarTemplate = (template) => {
  Object.keys(valoresPricing).forEach(key => delete valoresPricing[key])

  const atributos = template.estruturaJsonSchema ? JSON.parse(template.estruturaJsonSchema) : []
  pricingFields.value = atributos

  atributos.forEach(attr => {
    valoresPricing[attr.chave] = null
  })
}

const confirmarTrocaTemplate = (confirmed) => {
  if (confirmed && templateTemporario.value) {
    aplicarTemplate(templateTemporario.value)
  }
}

const modalConfirmacaoTrocaRef = ref(null)

// ============================================================
// SELEÇÃO DE PURCHASE ORDER NO GRID
// ============================================================
const selecionarPurchaseOrder = async (po) => {
  if (po.idPurchaseOrder === item.value.idPurchaseOrder) return

  const action = async () => {
    alterarFuncao('edicao')
    // Ao selecionar uma PO existente, sai do modo espera
    prePreencherValor.value = false

    // Busca os dados frescos da purchase order na API para garantir que todos os campos
    // (incluindo idOperacao) estejam atualizados, evitando usar dados obsoletos do grid
    let poAtualizada = po
    try {
      const res = await api.get(`/PurchaseOrders/${po.idPurchaseOrder}`)
      poAtualizada = res.data
    } catch (e) {
      console.warn('Erro ao buscar dados frescos da purchase order, usando dados do grid:', e)
    }

    // Se já estamos no modo drilldown de purchase order, apenas atualizamos o item atual em vez de empilhar
    if (props.drilldownMode && props.pipelineProps?.etapaAtiva === 'purchaseOrder') {
      initializeForm(poAtualizada)
      emit('itemAtualizado', poAtualizada)
      return
    }

    emit('drilldown-push', {
      type: 'purchaseOrder',
      id: po.idPurchaseOrder,
      props: {
        itemParaEditar: poAtualizada,
        contextoPai: props.contextoPai,
        prePreencher: false
      }
    })
  }

  if (hasChanges.value) {
    pendingAction.value = action
    confirmationModalRef.value.show()
  } else {
    await action()
  }
}

const iniciarNovaPurchaseOrder = () => {
  // Se está no fluxo do contrato e prePreencher ainda não foi definido,
  // mostra o modal ANTES de limpar o formulário para não perder o estado "aguardando decisão"
  if (idContratoEfetivo.value && prePreencherValor.value === null) {
    if (prePreencherModalRef.value) {
      prePreencherModalRef.value.show()
    }
    return
  }

  const action = () => {
    console.log('[DEBUG iniciarNovaPurchaseOrder] action executada, prePreencherValor=', prePreencherValor.value)
    // Limpa dados do formulário
    initializeForm(props.modeloVazio)
    // Garantir que o idContratoCompra seja mantido na nova purchase order
    if (idContratoEfetivo.value) {
      item.value.idContratoCompra = idContratoEfetivo.value
      initializeForm(item.value)
    }
    // Limpa dados das abas de documentos, produtos e pricing
    documentosEntrada.value = []
    produtosPurchaseOrder.value = []
    Object.keys(valoresPricing).forEach(key => delete valoresPricing[key])
    pricingFields.value = []
    purchaseOrdersGridAberto.value = false
    alterarFuncao('adicao')
    emit('itemAtualizado', item.value)
  }

  if (hasChanges.value) {
    pendingAction.value = action
    confirmationModalRef.value.show()
  } else {
    action()
  }
}

const onConfirmTransition = (confirmed) => {
  if (confirmed && pendingAction.value) {
    pendingAction.value()
  }
  pendingAction.value = null
}

const onCloseTransition = () => {
  pendingAction.value = null
}

const handleCancel = () => {
  console.log('[DEBUG handleCancel] hasChanges=', hasChanges.value, 'item=', JSON.stringify(item.value))
  const action = () => {
    if (props.drilldownMode) {
      emit('drilldown-pop')
    } else {
      emit('cancelar')
    }
  }
  if (hasChanges.value) {
    pendingAction.value = action
    confirmationModalRef.value?.show()
    return
  }
  action()
}

// GUARDIÃO: Camada de Validação
const validarRegrasDeNegocio = async () => {
  const labelsObrigatorios = document.querySelectorAll('label.is-required')
  let hasBaseErrors = false

  for (const key in errors.value) {
    delete errors.value[key]
  }

  labelsObrigatorios.forEach(label => {
    const inputId = label.getAttribute('for')
    if (!inputId) return
    const inputElement = document.getElementById(inputId)
    if (!inputElement) return

    let isEmpty = false
    if (inputElement.tagName === 'SELECT') {
      isEmpty = !inputElement.value || inputElement.value === ''
    } else {
      isEmpty = !inputElement.value || String(inputElement.value).trim() === ''
    }

    if (isEmpty) {
      const nomeCampoLimpo = label.innerText.replace('*', '').trim()
      errors.value[inputId] = `O campo "${nomeCampoLimpo}" é de preenchimento obrigatório.`
      hasBaseErrors = true
    }
  })

  if (hasBaseErrors) {
    emit('show-notification', { message: 'Por favor, preencha todos os campos obrigatórios destacados em vermelho.', type: 'warning' })
    if (focarPrimeiroErro) await focarPrimeiroErro()
    return false
  }

  return true
}

// Normaliza campos nullable decimal que podem vir como string vazia do formulário
// (ex: CampoInput type="number" emite "" quando limpo, mas o backend espera null)
const normalizarDecimal = (val) => {
  if (val === '' || val === undefined || val === null) return null
  const num = Number(String(val).replace(',', '.'))
  return isNaN(num) ? null : num
}

const montarPayload = () => {
  // Normaliza campos nullable int que podem vir como string vazia do formulário
  const normalizarInt = (val) => {
    if (val === '' || val === undefined || val === null) return null
    const num = Number(val)
    return isNaN(num) ? null : num
  }

  const payload = { ...item.value }

  // Remove arrays filhos gerenciados por endpoints dedicados
  // (Regra 17 — Independência de Entidades: cada entidade tem seu próprio endpoint).
  // Se incluídos no PUT, o backend faria RemoveRange + re-add com dados stale,
  // causando perda de registros recem-criados.
  delete payload.produtosSolicitados
  delete payload.condicoesPagamento
  delete payload.clausulas

  // idContratoCompra: prioriza o valor do item (fonte da verdade = banco),
  // fallback para contextoPai apenas em criação (quando item ainda não tem o vínculo).
  // NUNCA sobrescrever com undefined — isso desvincularia a PO do contrato.
  payload.idContratoCompra = item.value.idContratoCompra || props.contextoPai?.idContratoCompra || null
  payload.idGrupoAcessoAprovador = normalizarInt(item.value.idGrupoAcessoAprovador)
  payload.idUsuarioAprovador = normalizarInt(item.value.idUsuarioAprovador)
  payload.idTemplatePrecificacao = normalizarInt(item.value.idTemplatePrecificacao)
  payload.idLocalEstoquePrevisto = normalizarInt(item.value.idLocalEstoquePrevisto)
  payload.percentualTolerancia = normalizarDecimal(item.value.percentualTolerancia)

  return payload
}

const salvarEContinuar = async () => {
  if (!temPurchaseOrdersCompleto.value || isLoading.value) return

  // Auto-preenche dataEmissao se vazia
  if (!item.value.dataEmissao) {
    item.value.dataEmissao = new Date().toISOString().split('T')[0]
  }
  // Auto-preenche previsaoProducao se vazia (padrão: hoje + 30 dias)
  if (!item.value.previsaoProducao) {
    const padrao = new Date()
    padrao.setDate(padrao.getDate() + 30)
    item.value.previsaoProducao = padrao.toISOString().split('T')[0]
  }

  const isValid = await validarRegrasDeNegocio()
  if (!isValid) return

  isLoading.value = true
  const payload = montarPayload()

  try {
    let res

    // Se o usuário optou por pré-preenchimento, usa o endpoint dedicado
    // enviando também os campos editados pelo usuário no frame "Dados Básicos"
    if (prePreencherValor.value === true && idContratoEfetivo.value) {
      res = await api.post('/PurchaseOrders/from-contrato', {
        idContratoCompra: idContratoEfetivo.value,
        idOperacao: item.value.idOperacao,
        nomeReferencia: item.value.nomeReferencia,
        numeroProformaFornecedor: item.value.numeroProformaFornecedor,
        dataEmissao: item.value.dataEmissao,
        previsaoProducao: item.value.previsaoProducao,
        previsaoEmbarqueETD: item.value.previsaoEmbarqueETD,
        previsaoEmbarqueETA: item.value.previsaoEmbarqueETA,
        observacoes: item.value.observacoes,
        percentualTolerancia: normalizarDecimal(item.value.percentualTolerancia),
        idTemplatePrecificacao: item.value.idTemplatePrecificacao,
        idLocalEstoquePrevisto: item.value.idLocalEstoquePrevisto,
        lstMoedaGerencial: item.value.lstMoedaGerencial,
        taxaCambioGerencialPadrao: item.value.taxaCambioGerencialPadrao,
        lstTipoTaxaConversao: item.value.lstTipoTaxaConversao,
        dataAlvoBuscaCambio: item.value.dataAlvoBuscaCambio,
        dataReferenciaCambio: item.value.dataReferenciaCambio,
        lstTaxaCambioStatus: item.value.lstTaxaCambioStatus,
        lstFonteCambio: item.value.lstFonteCambio,
        lstMoedaPadrao: item.value.lstMoedaPadrao,
        taxaCambioPadrao: item.value.taxaCambioPadrao,
        atributosPrecificacaoJson: item.value.atributosPrecificacaoJson
      })
      emit('show-notification', {
        message: 'Purchase Order criada com os dados herdados do contrato.',
        type: 'success'
      })
    } else {
      res = item.value.idPurchaseOrder
        ? await api.put(`/PurchaseOrders/${item.value.idPurchaseOrder}`, payload)
        : await api.post('/PurchaseOrders', payload)

      emit('show-notification', { message: 'Dados básicos da purchase order salvos com sucesso! Agora você pode adicionar produtos.', type: 'success' })
    }

    initializeForm(res.data)
    await carregarPurchaseOrders()
  } catch (error) {
    console.error('Erro ao salvar purchase order:', error)
    const data = error.response?.data
    let msg = 'Erro ao salvar a purchase order.'
    if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

// ============================================================
// VALIDAÇÃO E APROVAÇÃO (Gap 7: sem Confirmar)
// ============================================================
const isLoadingAcao = ref(false)

const podeSolicitarAprovacao = computed(() => {
  return isPurchaseOrderSaved.value &&
    temPurchaseOrdersCompleto.value &&
    item.value.lstStatusPurchaseOrder === PO_ST.EmDigitacao
})

const isLiberadaParaDocumentos = computed(() => {
  const status = item.value.lstStatusPurchaseOrder
  // O frame de Documento de Entrada é liberado da Emitida (seq 4) até EncerradaParcialmente (seq 6).
  // O grid fica visível para consulta, mas o botão de novo documento é bloqueado via :pode-editar.
  const seq = PO_ST.sequencia[status] || 0
  return seq >= PO_ST.sequencia[PO_ST.Emitida] && seq <= PO_ST.sequencia[PO_ST.EncerradaParcialmente]
})

// ============================================================
// WORKFLOW DE APROVAÇÃO — Fluxo via WorkflowAprovacaoGrid
// O Grid gerencia o modal de preenchimento da solicitação.
// O formulário apenas persiste a PO e dispara o fluxo.
// ============================================================
const solicitacaoPreenchida = ref(null)

const handleSolicitacaoPreenchida = (dados) => {
  solicitacaoPreenchida.value = dados
}

const solicitarAprovacao = () => {
  if (isLoadingAcao.value || !item.value.idPurchaseOrder) return
  confirmSolicitarAprovacaoRef.value?.show()
}

const onSolicitarAprovacaoConfirmado = async (confirmed) => {
  if (!confirmed || isLoadingAcao.value || !item.value.idPurchaseOrder) return

  isLoadingAcao.value = true
  try {
    // Persiste a PO antes de enviar (garante que o backend valide os dados atuais)
    const payload = montarPayload()
    await api.put(`/PurchaseOrders/${item.value.idPurchaseOrder}`, payload)

    // Chama o endpoint de solicitar aprovação
    // Os dados de aprovação (grupo aprovador, resumo) já foram preenchidos
    // pelo usuário via modal do WorkflowAprovacaoGrid (PUT /WorkflowAprovacaoAnalise/{id})
    // e validados contra a solicitação em status Criada.
    await api.post(`/PurchaseOrders/${item.value.idPurchaseOrder}/solicitar-aprovacao`, {
      atributosPrecificacaoJson: item.value.atributosPrecificacaoJson || null
    })

    // Recarrega os dados atualizados do backend
    const res = await api.get(`/PurchaseOrders/${item.value.idPurchaseOrder}`)
    initializeForm(res.data)
    emit('itemAtualizado', res.data)
    await carregarPurchaseOrders()
    if (workflowGridRef.value) workflowGridRef.value.recarregar()
    emit('show-notification', { message: 'Aprovação solicitada com sucesso!', type: 'success' })
  } catch (e) {
    const msg = processarErrosValidacaoPO(e.response?.data) || 'Erro ao solicitar aprovação.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingAcao.value = false
  }
}

const nomeFornecedor = ref('')

// ============================================================
// AÇÃO: Emitir PO (Aprovada → Emitida)
// ============================================================
const emitirPO = () => {
  if (isLoadingAcao.value || !item.value.idPurchaseOrder) return
  confirmEmitirRef.value?.show()
}

const onEmitirConfirmado = async (confirmed) => {
  if (!confirmed || isLoadingAcao.value || !item.value.idPurchaseOrder) return
  isLoadingAcao.value = true
  try {
    const res = await api.post(`/PurchaseOrders/${item.value.idPurchaseOrder}/emitir`)
    initializeForm(res.data)
    await carregarPurchaseOrders()
    emit('show-notification', { message: 'Purchase Order emitida com sucesso!', type: 'success' })
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao emitir purchase order.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingAcao.value = false
  }
}

// ============================================================
// RELATÓRIO GERENCIAL — Template Paged.js (uso interno)
// ============================================================
const showRelatorioGerencialModal = ref(false)
const templatesRelatorioGerencial = ref([])
const clausulasRelatorioGerencial = ref([])

// Relatório para Fornecedor (Inglês)
const showRelatorioFornecedorInglesModal = ref(false)
const templatesRelatorioFornecedorIngles = ref([])
const clausulasRelatorioFornecedorIngles = ref([])
const dadosRelatorioFornecedorIngles = ref(null)

// Rótulos de visibilidade das cláusulas (constante estrutural CLAUSULAS_COM_VISIB)
const LABELS_VISIBILIDADE_CLAUSULA = {
  [CLAUSULAS_COM_VISIB.Interna]: 'Interna',
  [CLAUSULAS_COM_VISIB.Externa]: 'Externa',
  [CLAUSULAS_COM_VISIB.Ambas]: 'Ambas'
}

const isClausulaExterna = (codigo) =>
  codigo === CLAUSULAS_COM_VISIB.Externa || codigo === CLAUSULAS_COM_VISIB.Ambas

const dadosRelatorioGerencial = computed(() => {
  const i = item.value

  return {
    // Identificação
    numeroPurchaseOrder: i.numeroPurchaseOrder || '',
    nomeReferencia: i.nomeReferencia || '',
    numeroProformaFornecedor: i.numeroProformaFornecedor || '',
    numeroContratoFornecedor: i.numeroContratoFornecedor || '',
    statusTexto: getTextoLista(listas.LstStatusPurchaseOrder, i.lstStatusPurchaseOrder),
    dataEmissao: formatarDataBR(i.dataEmissao),

    // Partes e dados comerciais
    nomeFornecedor: i.nomeFornecedor || nomeFornecedor.value || '-',
    nomeOperacao: i.nomeOperacao || '-',
    nomeFilial: i.nomeFilial || '-',
    incoterm: getTextoLista(listas.LstIncoterm, i.lstIncoterm),
    localIncoterm: getTextoLista(listas.LstLocalIncoterm, i.lstLocalIncoterm),
    formaPagamento: getTextoLista(listas.LstFormaPagamento, i.lstFormaPagamento),
    moeda: getTextoLista(listas.LstMoeda, i.lstMoeda),

    // Cronograma
    previsaoProducao: formatarDataBR(i.previsaoProducao),
    previsaoEmbarqueETD: formatarDataBR(i.previsaoEmbarqueETD),
    previsaoEmbarqueETA: formatarDataBR(i.previsaoEmbarqueETA),

    // Produtos
    produtos: (produtosPurchaseOrder.value || []).map(p => ({
      sku: p.sku || '',
      nome: p.nomeProduto || `Produto #${p.idProduto}`,
      quantidadeFornecedor: Number(p.quantidadeUnFornecedorNegociada) || 0,
      unidadeFornecedor: getTextoUnidadeRelatorio(p.lstUnidadeMedidaFornecedor),
      quantidadeBase: Number(p.quantidadeUnBasicaNegociada) || 0,
      unidadeBase: getTextoUnidadeRelatorio(p.lstUnidadeMedidaBasica),
      valorUnitario: Number(p.valorUnitarioOriginal) || 0,
      valorTotal: (Number(p.quantidadeUnFornecedorNegociada) || 0) * (Number(p.valorUnitarioOriginal) || 0),
      statusProdutoTexto: getTextoLista(listas.LstStatusProdutoPo, p.lstStatusProdutoPo),
      motivoDescontoTexto: getTextoLista(listas.LstMotivoDesconto, p.lstMotivoDesconto),
      observacao: p.observacao || ''
    })),

    // Composição de valores (moeda da operação)
    totais: {
      original: Number(i.valorTotalOriginal) || 0,
      desconto: Number(i.valorTotalDesconto) || 0,
      frete: Number(i.valorTotalFrete) || 0,
      negociado: Number(i.valorTotalNegociado) || 0
    },

    // Trilho cambial
    cambio: {
      moedaPadrao: getTextoLista(listas.LstMoedaPadrao, i.lstMoedaPadrao),
      taxaCambioPadrao: i.taxaCambioPadrao,
      originalPadrao: i.valorTotalOriginalPadrao,
      descontoPadrao: i.valorTotalDescontoPadrao,
      fretePadrao: i.valorTotalFretePadrao,
      negociadoPadrao: i.valorTotalNegociadoPadrao,
      moedaGerencial: getTextoLista(listas.LstMoedaGerencial, i.lstMoedaGerencial),
      taxaCambioGerencial: i.taxaCambioGerencial,
      taxaCambioGerencialPadrao: i.taxaCambioGerencialPadrao,
      originalGerencial: i.valorTotalOriginalGerencial,
      descontoGerencial: i.valorTotalDescontoGerencial,
      freteGerencial: i.valorTotalFreteGerencial,
      negociadoGerencial: i.valorTotalNegociadoGerencial,
      tipoTaxaConversaoTexto: getTextoLista(listas.LstTipoTaxaConversao, i.lstTipoTaxaConversao),
      statusTaxaTexto: getTextoLista(listas.LstTaxaCambioStatus, i.lstTaxaCambioStatus),
      dataReferenciaCambio: formatarDataBR(i.dataReferenciaCambio),
      fonteCambioTexto: getTextoLista(listas.LstFonteCambio, i.lstFonteCambio)
    },

    // Condições de pagamento
    condicoesPagamento: (condicoesPagamento.value || []).map(c => ({
      descricao: c.descricao || gerarDescricaoCondicao(c),
      percentualDoTotal: Number(c.percentualDoTotal) || 0,
      referenciaTemporalTexto: getTextoLista(listas.LstReferenciaTemporal, c.lstReferenciaTemporal),
      prazoDias: c.prazoDias
    })),

    // Cláusulas (TODAS — Internas + Externas)
    clausulas: (clausulasRelatorioGerencial.value || []).map(c => ({
      titulo: c.titulo || '',
      texto: c.texto || '',
      ordemExibicao: c.ordemExibicao || 0,
      origemDescricao: c.origemDescricao || '',
      visibilidadeTexto: LABELS_VISIBILIDADE_CLAUSULA[c.lstVisibilidade] || '',
      isExterna: isClausulaExterna(c.lstVisibilidade)
    })),

    // Aprovação (campos persistidos na PurchaseOrder)
    aprovacao: {
      nomeGrupoAprovador: i.nomeGrupoAprovador || (i.idGrupoAcessoAprovador ? `Grupo #${i.idGrupoAcessoAprovador}` : ''),
      nomeUsuarioAprovador: i.nomeUsuarioAprovador || (i.idUsuarioAprovador ? `Usuário #${i.idUsuarioAprovador}` : ''),
      resumoAprovacao: i.resumoAprovacao || '',
      observacaoReprovacao: i.observacaoReprovacao || ''
    },

    // Observações
    observacoes: i.observacoes || '',

    // Auditoria
    auditoria: {
      nomeUsuarioCriacao: i.nomeUsuarioCriacao || '',
      tsCriacao: formatarDataHoraBR(i.tsCriacao)
    }
  }
})

const gerarRelatorioGerencial = async () => {
  if (!isPurchaseOrderSaved.value) {
    emit('show-notification', { message: 'Salve a Purchase Order antes de gerar o relatório.', type: 'warning' })
    return
  }
  try {
    // Carrega as cláusulas vinculadas (com visibilidade resolvida no backend)
    try {
      const resClausulas = await api.get(`/PurchaseOrderClausulas/por-po/${item.value.idPurchaseOrder}`)
      clausulasRelatorioGerencial.value = Array.isArray(resClausulas.data) ? resClausulas.data : []
    } catch (e) {
      clausulasRelatorioGerencial.value = item.value.clausulas || []
    }

    const [templatesRes, habilitadosRes] = await Promise.all([
      api.get(`/RelatorioTemplates/por-modulo/PURCHASE_ORDER?idEmpresa=${authStore.empresaAtiva}`),
      api.get(`/EmpresaRelatorioTemplates/empresa/${authStore.empresaAtiva}`)
    ])

    const templates = (templatesRes.data || []).map(t => {
      const hab = (habilitadosRes.data || []).find(h => String(h.idRelatorioTemplate) === String(t.idRelatorioTemplate))
      let imagensConfig = {}
      try {
        imagensConfig = hab?.configuracaoImagensJson ? JSON.parse(hab.configuracaoImagensJson) : {}
      } catch (e) { imagensConfig = {} }
      return { ...t, imagensConfig }
    })

    templatesRelatorioGerencial.value = templates
    if (templates.length === 0) {
      emit('show-notification', { message: 'Nenhum template de Relatório Gerencial disponível. Contate o administrador.', type: 'warning' })
      return
    }
    showRelatorioGerencialModal.value = true
  } catch (error) {
    console.error('Erro ao carregar templates do Relatório Gerencial:', error)
    emit('show-notification', { message: 'Erro ao carregar templates do Relatório Gerencial.', type: 'error' })
  }
}

// ============================================================
// RELATÓRIO PARA FORNECEDOR (INGLÊS) — Template Paged.js
// ============================================================
const formatarEnderecoParte = (parte) => {
  if (!parte) return ''
  const linha1 = [parte.logradouro, parte.numero].filter(Boolean).join(', ')
  const linha2 = [parte.complemento, parte.bairro].filter(Boolean).join(', ')
  const linha3 = [parte.cidade, parte.uf].filter(Boolean).join(' - ')
  const linha4 = [parte.cep, parte.pais].filter(Boolean).join(', ')
  return [linha1, linha2, linha3, linha4].filter(Boolean).join(', ')
}

/**
 * Monta o objeto de dados para o relatório do fornecedor em inglês.
 * Chamado dentro de gerarRelatorioFornecedorIngles() para evitar race condition
 * entre o carregamento assíncrono das listas en-US e a avaliação do computed.
 */
const montarDadosRelatorioFornecedorIngles = () => {
  const i = item.value
  const comprador = i.relatorioComprador || {}
  const fornecedor = i.relatorioFornecedor || {}

  return {
    // Identificação
    numeroPurchaseOrder: i.numeroPurchaseOrder || '',
    nomeReferencia: i.nomeReferencia || '',
    numeroProformaFornecedor: i.numeroProformaFornecedor || '',
    numeroContratoFornecedor: i.numeroContratoFornecedor || '',
    statusTexto: getTextoLista(listas.LstStatusPurchaseOrder, i.lstStatusPurchaseOrder),
    dataEmissao: formatarDataBR(i.dataEmissao),

    // Buyer (Importer)
    comprador: {
      nome: comprador.nome || i.nomeFilial || '',
      documento: comprador.documento || '',
      inscricaoEstadual: comprador.inscricaoEstadual || '',
      endereco: formatarEnderecoParte(comprador)
    },

    // Supplier (Exporter)
    fornecedor: {
      nome: fornecedor.nome || i.nomeFornecedor || '',
      razaoSocial: fornecedor.razaoSocial || '',
      documento: fornecedor.documento || '',
      email: fornecedor.email || '',
      telefone: fornecedor.telefone || '',
      endereco: formatarEnderecoParte(fornecedor)
    },

    // Commercial terms (textos resolvidos + códigos para template independente)
    incoterm: getTextoLista(listas.LstIncoterm, i.lstIncoterm),
    lstIncoterm: i.lstIncoterm || '',
    localIncoterm: getTextoLista(listas.LstLocalIncoterm, i.lstLocalIncoterm),
    formaPagamento: getTextoLista(listas.LstFormaPagamento, i.lstFormaPagamento),
    lstFormaPagamento: i.lstFormaPagamento || '',
    moeda: getTextoLista(listas.LstMoeda, i.lstMoeda),
    lstMoeda: i.lstMoeda || '',
    nomeOperacao: i.nomeOperacao || '',
    nomeFilial: i.nomeFilial || '',

    // Schedule
    previsaoProducao: formatarDataBR(i.previsaoProducao),
    previsaoEmbarqueETD: formatarDataBR(i.previsaoEmbarqueETD),
    previsaoEmbarqueETA: formatarDataBR(i.previsaoEmbarqueETA),

    // Line items
    produtos: (produtosPurchaseOrder.value || []).map(p => ({
      sku: p.sku || '',
      nome: p.nomeProdutoIngles || p.nomeProduto || `Product #${p.idProduto}`,
      quantidadeFornecedor: Number(p.quantidadeUnFornecedorNegociada) || 0,
      unidadeFornecedor: getTextoUnidadeRelatorio(p.lstUnidadeMedidaFornecedor),
      quantidadeBase: Number(p.quantidadeUnBasicaNegociada) || 0,
      unidadeBase: getTextoUnidadeRelatorio(p.lstUnidadeMedidaBasica),
      valorUnitario: Number(p.valorUnitarioOriginal) || 0,
      valorTotal: (Number(p.quantidadeUnFornecedorNegociada) || 0) * (Number(p.valorUnitarioOriginal) || 0),
      codigoNcm: p.codigoNcm || '',
      origem: (() => {
        if (p.descricaoPaisOrigem) return p.descricaoPaisOrigem
        const pais = (listas.LstPaisOrigem || []).find(l => String(l.idListaClienteItem) === String(p.lstPaisOrigem))
        return pais ? pais.textoLista : (p.lstPaisOrigem || '')
      })(),
      lstPaisOrigem: p.lstPaisOrigem || '',
      pesoLiquido: p.pesoLiquido,
      pesoBruto: p.pesoBruto
    })),

    // Totals
    totais: {
      original: Number(i.valorTotalOriginal) || 0,
      desconto: Number(i.valorTotalDesconto) || 0,
      frete: Number(i.valorTotalFrete) || 0,
      negociado: Number(i.valorTotalNegociado) || 0,
      negociadoPadrao: i.valorTotalNegociadoPadrao,
      moedaPadrao: getTextoLista(listas.LstMoedaPadrao, i.lstMoedaPadrao)
    },

    // Payment terms
    condicoesPagamento: (condicoesPagamento.value || []).map(c => ({
      descricao: c.descricao || gerarDescricaoCondicao(c),
      percentualDoTotal: Number(c.percentualDoTotal) || 0,
      referenciaTemporalTexto: getTextoLista(listas.LstReferenciaTemporal, c.lstReferenciaTemporal),
      prazoDias: c.prazoDias,
      // Códigos das listas para tradução en-US no template
      lstCondPgMomento: c.lstCondPgMomento || '',
      lstCondPgUnidadeTempo: c.lstCondPgUnidadeTempo || '',
      lstCondPgTipoCalculo: c.lstCondPgTipoCalculo || '',
      lstReferenciaTemporal: c.lstReferenciaTemporal || ''
    })),

    // Terms & Conditions (external clauses only — no visibility badge)
    clausulas: (clausulasRelatorioFornecedorIngles.value || [])
      .filter(c => isClausulaExterna(c.lstVisibilidade))
      .map(c => ({
        titulo: c.titulo || '',
        texto: c.texto || '',
        ordemExibicao: c.ordemExibicao || 0
      })),

    // Notes
    observacoes: i.observacoes || '',

    // Audit
    auditoria: {
      nomeUsuarioCriacao: i.nomeUsuarioCriacao || '',
      tsCriacao: formatarDataHoraBR(i.tsCriacao)
    }
  }
}

const gerarRelatorioFornecedorIngles = async () => {
  if (!isPurchaseOrderSaved.value) {
    emit('show-notification', { message: 'Salve a Purchase Order antes de gerar o relatório.', type: 'warning' })
    return
  }
  // Aguarda o carregamento inicial das listas
  await listasCarregadasPromise
  try {
    // Carrega as cláusulas vinculadas (com visibilidade resolvida no backend)
    try {
      const resClausulas = await api.get(`/PurchaseOrderClausulas/por-po/${item.value.idPurchaseOrder}`)
      clausulasRelatorioFornecedorIngles.value = Array.isArray(resClausulas.data) ? resClausulas.data : []
    } catch (e) {
      clausulasRelatorioFornecedorIngles.value = item.value.clausulas || []
    }

    // Popula os dados do relatório
    dadosRelatorioFornecedorIngles.value = montarDadosRelatorioFornecedorIngles()

    const [templatesRes, habilitadosRes] = await Promise.all([
      api.get(`/RelatorioTemplates/por-modulo/PURCHASE_ORDER_FORNECEDOR_INGLES?idEmpresa=${authStore.empresaAtiva}`),
      api.get(`/EmpresaRelatorioTemplates/empresa/${authStore.empresaAtiva}`)
    ])

    const templates = (templatesRes.data || []).map(t => {
      const hab = (habilitadosRes.data || []).find(h => String(h.idRelatorioTemplate) === String(t.idRelatorioTemplate))
      let imagensConfig = {}
      try {
        imagensConfig = hab?.configuracaoImagensJson ? JSON.parse(hab.configuracaoImagensJson) : {}
      } catch (e) { imagensConfig = {} }
      return { ...t, imagensConfig }
    })

    templatesRelatorioFornecedorIngles.value = templates
    if (templates.length === 0) {
      emit('show-notification', { message: 'Nenhum template de Relatório para Fornecedor (Inglês) disponível. Contate o administrador.', type: 'warning' })
      return
    }
    showRelatorioFornecedorInglesModal.value = true
  } catch (error) {
    console.error('Erro ao carregar templates do Relatório para Fornecedor (Inglês):', error)
    emit('show-notification', { message: 'Erro ao carregar templates do Relatório para Fornecedor (Inglês).', type: 'error' })
  }
}

const getTextoUnidadeRelatorio = (id) => {
  const lista = listas.LstUnidadeMedidaFornecedor || listas.LstUnidadeMedidaBase || listas.LstUnidadeMedidaBasica || []
  const un = lista.find(u => String(u.idListaClienteItem) === String(id))
  return un ? un.textoLista : (id || '')
}

const podeCancelarPO = computed(() => {
  const status = item.value.lstStatusPurchaseOrder
  // Cancelamento permitido apenas antes de ter documentos aprovados (FaturadaParcialmente, seq 5).
  // Usa o sequencial da lista PO_ST para garantir consistência.
  const seq = PO_ST.sequencia[status] || 99
  return isPurchaseOrderSaved.value &&
    seq < PO_ST.sequencia[PO_ST.FaturadaParcialmente]
})

const cancelarPO = () => {
  if (isLoadingAcao.value || !item.value.idPurchaseOrder) return
  confirmCancelarRef.value?.show()
}

const onCancelarConfirmado = async (confirmed) => {
  if (!confirmed || isLoadingAcao.value || !item.value.idPurchaseOrder) return
  isLoadingAcao.value = true
  try {
    await api.post(`/PurchaseOrders/${item.value.idPurchaseOrder}/cancelar`)
    const res = await api.get(`/PurchaseOrders/${item.value.idPurchaseOrder}`)
    item.value = res.data
    emit('itemAtualizado', res.data)
    await carregarPurchaseOrders()
    emit('show-notification', { message: 'Purchase Order cancelada com sucesso!', type: 'success' })
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao cancelar purchase order.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingAcao.value = false
  }
}

const salvar = async (e) => {
  if (e) {
    e.preventDefault()
  }
  if (!temPurchaseOrdersCompleto.value || isLoading.value) return

  const isValid = await validarRegrasDeNegocio()
  if (!isValid) return

  const payload = montarPayload()
  const success = await submitForm('PurchaseOrders', 'idPurchaseOrder', payload)
  if (success) {
    emit('show-notification', { message: 'Purchase Order salva com sucesso!', type: 'success' })
  }
}

const salvarAlteracoes = async () => {
  if (!temPurchaseOrdersCompleto.value || isLoading.value) return

  const isValid = await validarRegrasDeNegocio()
  if (!isValid) return

  const payload = montarPayload()
  try {
    const res = item.value.idPurchaseOrder
      ? await api.put(`/PurchaseOrders/${item.value.idPurchaseOrder}`, payload)
      : await api.post('/PurchaseOrders', payload)

    if (res?.data) {
      initializeForm(res.data)
    }
    emit('show-notification', { message: 'Alterações salvas com sucesso!', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar alterações.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// ============================================================
// CONDIÇÕES DE PAGAMENTO (Modal)
// ============================================================
const isLoadingCondicoes = ref(false)
const condicoesPagamento = ref([])
const condicaoEmEdicao = ref(null)
const modalExclusaoCondicao = ref({ id: null })
const modalExclusaoCondicaoRef = ref(null)

const showModalCondicao = computed({
  get: () => !!condicaoEmEdicao.value,
  set: (val) => { if (!val) condicaoEmEdicao.value = null }
})

const somaPercentuaisCondicoes = computed(() => {
  return condicoesPagamento.value.reduce((acc, linha) => acc + (Number(linha.percentualDoTotal) || 0), 0)
})

const percentualRestanteCondicoes = computed(() => {
  return Math.max(0, 100 - somaPercentuaisCondicoes.value)
})

const somaExcedidaCondicoes = computed(() => {
  return somaPercentuaisCondicoes.value > 100
})

const carregarCondicoes = async () => {
  if (!item.value.idPurchaseOrder) return
  isLoadingCondicoes.value = true
  try {
    const res = await api.get('/PurchaseOrderCondicoesPg', {
      params: { idPurchaseOrder: item.value.idPurchaseOrder, pageSize: 9999 }
    })
    condicoesPagamento.value = res.data
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar condições de pagamento.', type: 'error' })
  } finally {
    isLoadingCondicoes.value = false
  }
}

const getTextoReferenciaTemporal = (id) => {
  const lista = listas?.LstReferenciaTemporal || []
  return getTextoLista(lista, id)
}

const gerarDescricaoCondicao = (cond) => {
  if (!cond) return ''
  const prazo = Math.abs(Number(cond.prazoDias) || 0)
  const refTemporal = getTextoReferenciaTemporal(cond.lstReferenciaTemporal)
  const percentual = cond.percentualDoTotal || 0
  const momento = getTextoLista(listas.LstCondPgMomento, cond.lstCondPgMomento)
  const unidade = getTextoLista(listas.LstCondPgUnidadeTempo, cond.lstCondPgUnidadeTempo)
  const tipoCalc = getTextoLista(listas.LstCondPgTipoCalculo, cond.lstCondPgTipoCalculo)
  return `${percentual}${tipoCalc} ${prazo} ${unidade} ${momento} ${refTemporal}`
}

const iniciarNovaCondicao = () => {
  if (condicoesPagamentoBloqueadas.value) return
  if (somaPercentuaisCondicoes.value >= 100) {
    emit('show-notification', { message: 'Já atingiu 100% de alocação. Não é possível adicionar mais condições.', type: 'warning' })
    return
  }
  condicaoEmEdicao.value = {
    idPurchaseOrderCondicaoPg: 0,
    idPurchaseOrder: item.value.idPurchaseOrder,
    percentualDoTotal: null,
    descricao: '',
    lstReferenciaTemporal: '',
    lstCondPgMomento: 'COND_PG_MOMENTO1',
    lstCondPgUnidadeTempo: 'COND_PG_UNIDADE_TEMPO1',
    lstCondPgTipoCalculo: 'COND_PG_TIPO_CALCULO1',
    prazoDias: null
  }
}

const editarCondicao = (cond) => {
  if (condicoesPagamentoBloqueadas.value) return
  condicaoEmEdicao.value = { ...cond }
}

const salvarCondicao = async () => {
  if (condicoesPagamentoBloqueadas.value) return
  const c = { ...condicaoEmEdicao.value }
  if (!c.lstReferenciaTemporal || c.percentualDoTotal === null) {
    emit('show-notification', { message: 'Preencha todos os campos obrigatórios da condição de pagamento.', type: 'warning' })
    return
  }

  // Valida soma dos percentuais
  const somaSemAtual = condicoesPagamento.value
    .filter(l => l.idPurchaseOrderCondicaoPg !== c.idPurchaseOrderCondicaoPg)
    .reduce((acc, l) => acc + (Number(l.percentualDoTotal) || 0), 0)
  const totalComAtual = somaSemAtual + (Number(c.percentualDoTotal) || 0)
  if (totalComAtual > 100) {
    emit('show-notification', { message: `A soma dos percentuais não pode exceder 100%. Atualmente ${totalComAtual.toFixed(2)}% alocado.`, type: 'warning' })
    return
  }

  c.descricao = gerarDescricaoCondicao(c)

  const payload = {
    idPurchaseOrder: item.value.idPurchaseOrder,
    percentualDoTotal: Number(c.percentualDoTotal) || 0,
    descricao: c.descricao || '',
    lstReferenciaTemporal: c.lstReferenciaTemporal,
    lstCondPgMomento: c.lstCondPgMomento || 'COND_PG_MOMENTO1',
    lstCondPgUnidadeTempo: c.lstCondPgUnidadeTempo || 'COND_PG_UNIDADE_TEMPO1',
    lstCondPgTipoCalculo: c.lstCondPgTipoCalculo || 'COND_PG_TIPO_CALCULO1',
    prazoDias: Number(c.prazoDias) || 0
  }

  isLoadingCondicoes.value = true
  try {
    if (c.idPurchaseOrderCondicaoPg) {
      await api.put(`/PurchaseOrderCondicoesPg/${c.idPurchaseOrderCondicaoPg}`, payload)
    } else {
      await api.post('/PurchaseOrderCondicoesPg', payload)
    }
    emit('show-notification', { message: 'Condição de pagamento salva com sucesso!', type: 'success' })
    condicaoEmEdicao.value = null
    await carregarCondicoes()
  } catch (error) {
    const errorMsg = error.response?.data?.message || 'Erro ao salvar condição de pagamento.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  } finally {
    isLoadingCondicoes.value = false
  }
}

const solicitarExclusaoCondicao = (cond) => {
  if (condicoesPagamentoBloqueadas.value) return
  modalExclusaoCondicao.value.id = cond.idPurchaseOrderCondicaoPg
  nextTick(() => {
    if (modalExclusaoCondicaoRef.value) {
      modalExclusaoCondicaoRef.value.show()
    }
  })
}

const confirmarExclusaoCondicao = async (confirmed) => {
  if (!confirmed) return
  isLoadingCondicoes.value = true
  try {
    await api.delete(`/PurchaseOrderCondicoesPg/${modalExclusaoCondicao.value.id}`)
    emit('show-notification', { message: 'Condição de pagamento removida com sucesso!', type: 'success' })
    await carregarCondicoes()
  } catch (error) {
    const errorMsg = error.response?.data?.message || 'Erro ao remover condição de pagamento.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  } finally {
    isLoadingCondicoes.value = false
    modalExclusaoCondicao.value.id = null
  }
}

// Baseline da sincronização de cláusulas — evita disparar o endpoint
// sincronizar-clausulas no carregamento inicial (que deletava cláusulas
// por comparar listas vazias/obsoletas).
let ultimoFornecedorSync = null
let ultimosIdsProdutosSync = null

watch(() => item.value.idPurchaseOrder, (newId, _oldId) => {
  isPurchaseOrderSaved.value = !!(newId && newId > 0)
  // Ao trocar de PO, reseta o baseline para que a próxima carga de produtos
  // seja tratada como carregamento inicial (sem sincronização).
  ultimoFornecedorSync = null
  ultimosIdsProdutosSync = null
  if (isPurchaseOrderSaved.value) {
    carregarProdutosPurchaseOrder()
    carregarProdutosContrato()
    sincronizarJsonParaValores()
    carregarCondicoes()
  }
}, { immediate: true })

const isReadOnly = computed(() => {
  if (!isEditing.value) return false
  if (!temPurchaseOrdersCompleto.value) return true
  return item.value.lstStatusPurchaseOrder === PO_ST.EmAprovacao
})

// Indica se a PO já foi aprovada (status >= Aprovada).
// Após aprovação, apenas Observações e Câmbio podem ser alterados.
const isPosAprovacao = computed(() => {
  const status = item.value.lstStatusPurchaseOrder
  return status && status !== PO_ST.EmDigitacao && status !== PO_ST.EmAprovacao
})

const podeEmitirPO = computed(() => {
  return isPurchaseOrderSaved.value &&
    temPurchaseOrdersCompleto.value &&
    item.value.lstStatusPurchaseOrder === PO_ST.Aprovada
})

const podeEncerrarPO = computed(() => {
  return isPurchaseOrderSaved.value &&
    temPurchaseOrdersCompleto.value &&
    item.value.lstStatusPurchaseOrder === PO_ST.EmbarcadaParcial
})

// eslint-disable-next-line no-unused-vars -- usada no template @click
const podeEncerrarParcialmente = computed(() => {
  return isPurchaseOrderSaved.value &&
    temPurchaseOrdersCompleto.value &&
    item.value.lstStatusPurchaseOrder === PO_ST.FaturadaParcialmente
})

const encerrarPO = () => {
  if (isLoadingAcao.value || !item.value.idPurchaseOrder) return
  // Decide qual modal mostrar baseado no status atual
  if (item.value.lstStatusPurchaseOrder === PO_ST.FaturadaParcialmente) {
    confirmEncerrarParcialmenteRef.value?.show()
  } else {
    confirmEncerrarRef.value?.show()
  }
}

const onEncerrarConfirmado = async (confirmed) => {
  if (!confirmed || isLoadingAcao.value || !item.value.idPurchaseOrder) return
  isLoadingAcao.value = true
  try {
    const res = await api.post(`/PurchaseOrders/${item.value.idPurchaseOrder}/encerrar`)
    initializeForm(res.data)
    await carregarPurchaseOrders()
    emit('show-notification', { message: 'Purchase Order encerrada com sucesso!', type: 'success' })
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao encerrar purchase order.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingAcao.value = false
  }
}

const podeRetornarPendente = computed(() => {
  return isPurchaseOrderSaved.value &&
    temPurchaseOrdersCompleto.value &&
    item.value.lstStatusPurchaseOrder === PO_ST.Reprovada
})

const retornarPendente = () => {
  if (isLoadingAcao.value || !item.value.idPurchaseOrder) return
  confirmRetornarPendenteRef.value?.show()
}

const onRetornarPendenteConfirmado = async (confirmed) => {
  if (!confirmed || isLoadingAcao.value || !item.value.idPurchaseOrder) return
  isLoadingAcao.value = true
  try {
    const res = await api.post(`/PurchaseOrders/${item.value.idPurchaseOrder}/retornar-pendente`)
    initializeForm(res.data)
    await carregarPurchaseOrders()
    emit('show-notification', { message: 'Purchase Order retornada para Pendente com sucesso!', type: 'success' })
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao retornar purchase order para pendente.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingAcao.value = false
  }
}

// Indica se a PO está em status terminal (EncerradaParcialmente, Encerrada, Reprovada, Cancelada).
// Nestes status, NADA pode ser alterado — nem mesmo Observações.
const isTerminal = computed(() => {
  const status = item.value.lstStatusPurchaseOrder
  const seq = PO_ST.sequencia[status] || 0
  return seq >= PO_ST.sequencia[PO_ST.EncerradaParcialmente]
})

// Desabilita campos individuais nas abas quando está pós-aprovação
const campoDesabilitado = computed(() => {
  return !podeEditar.value || modoEspera.value || isPosAprovacao.value
})

// Campos que PERMANECEM editáveis mesmo pós-aprovação.
// Usado pelo Nº Proforma do Fornecedor e Observações: na prática o fornecedor
// só envia a proforma após o envio da PO, que ocorre depois da aprovação.
// Em status terminais (EncerradaParcialmente+), nada é editável.
const campoDesabilitadoPosAprovacao = computed(() => {
  return !podeEditar.value || modoEspera.value || isTerminal.value
})

// Condições de pagamento são imutáveis após a PO sair de Em Digitação:
// bloqueadas durante Em Aprovação (isReadOnly) e em todos os status pós-aprovação (isPosAprovacao).
const condicoesPagamentoBloqueadas = computed(() => {
  return !podeEditar.value || modoEspera.value || isPosAprovacao.value || isReadOnly.value
})

onMounted(async () => {
  // Sincroniza a função inicial baseada na prop funcao e no itemParaEditar
  if (props.funcao === 'adicao') {
    alterarFuncao('adicao')
    purchaseOrdersGridAberto.value = false
  } else if (props.itemParaEditar?.idPurchaseOrder) {
    alterarFuncao('edicao')
  } else {
    alterarFuncao('edicao')
  }

  initialLoadDone.value = true

  await carregarListas()

  // Se não temos um contrato no item mas temos no contexto/pipeline, inicializa
  // Nota: com a nova lógica, idContratoEfetivo prioriza item.value.idContratoCompra,
  // então esta condição só será verdadeira em criação (item sem idContratoCompra).
  if (!item.value.idContratoCompra && idContratoEfetivo.value) {
    item.value.idContratoCompra = idContratoEfetivo.value
    // Reset baseline after initial assignment to avoid 'dirty' state immediately
    initializeForm(item.value)
  }

  // Carrega as purchase orders do contrato pai para exibir no grid superior
  await carregarPurchaseOrders()

  if (isPurchaseOrderSaved.value) {
    await carregarProdutosPurchaseOrder()
    await carregarProdutosContrato()
    sincronizarJsonParaValores()
  } else if (idContratoEfetivo.value) {
    // Se for uma nova purchase order mas viemos de um contrato, carregamos os produtos do contrato para o modal de seleção
    await carregarProdutosContrato()
  }
})

// ============================================================
// REFERÊNCIA PARA O CLAUSULASGRID
// ============================================================
const clausulasGridRef = ref(null)

// Watch para sincronizar cláusulas quando fornecedor/produtos mudam.
// Guarda: a primeira execução (carregamento inicial) apenas registra o baseline
// e NÃO chama o endpoint — evita deletar cláusulas por comparar listas vazias.
watch([() => item.value.idParceiroNegocioFornecedor, () => produtosPurchaseOrder.value], async ([novoFornecedor, produtos]) => {
  if (!isPurchaseOrderSaved.value || !produtos?.length) return

  const idsAtuais = produtos
    .map(p => p.idProduto)
    .filter(id => id != null && id !== undefined)
  if (idsAtuais.length === 0) return

  // Primeira execução: registra o baseline e sai sem sincronizar
  if (ultimoFornecedorSync === null && ultimosIdsProdutosSync === null) {
    ultimoFornecedorSync = novoFornecedor
    ultimosIdsProdutosSync = idsAtuais
    return
  }

  const fornecedorMudou = novoFornecedor !== ultimoFornecedorSync
  const produtosMudaram = JSON.stringify(idsAtuais) !== JSON.stringify(ultimosIdsProdutosSync)
  ultimoFornecedorSync = novoFornecedor
  ultimosIdsProdutosSync = idsAtuais

  // Nada mudou de fato (ex: re-render) — não sincroniza
  if (!fornecedorMudou && !produtosMudaram) return

  try {
    await api.post(`/PurchaseOrders/${item.value.idPurchaseOrder}/sincronizar-clausulas`, {
      idParceiroNegocioFornecedor: novoFornecedor,
      idsProduto: idsAtuais
    })
    if (clausulasGridRef.value) {
      await clausulasGridRef.value.recarregar()
    }
  } catch (e) {
    // Silencioso — backend já trata
  }
})
</script>

<template>
  <div class="purchase-order-form-wrapper" v-bind="$attrs">
    <SemAcessoModulo v-if="!temModuloImportacao" titulo="Importação" />
    <template v-if="temModuloImportacao">
    <FormularioCadastro
      module-name="Purchase Order"
      :is-loading="isLoading"
      :is-editing="isEditing"
      :entity-info="entityInfoPurchaseOrder"
      :titulo-registro="item.nomeReferencia"
      :ocultar-salvar="!isPurchaseOrderSaved || !temPurchaseOrdersCompleto"
      :accordion-state="accordionState"
      @submit="salvar"
      @submit-continuar="salvarAlteracoes"
      @cancelar="handleCancel"
      @show-notification="(p) => emit('show-notification', p)"
    >
      <template #acoes-header v-if="isEditing">
        <button
          type="button"
          class="btn-icon-minimalista me-3"
          @click="mostrarAuditoria = true"
          title="Ver Histórico de Alterações"
        >
          <i class="bi bi-clock-history"></i>
        </button>
      </template>

      <ComexPipelineStatus
        v-if="drilldownMode"
        v-bind="pipelineProps"
        :idPurchaseOrder="item.idPurchaseOrder"
        @navegar="(e) => emit('pipeline-navegar', e.key)"
      />

      <!-- MODO ESPERA: Aviso de que é necessário decidir sobre pré-preenchimento -->
      <div v-if="modoEspera" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
        <i class="bi bi-exclamation-triangle-fill fs-4 me-3 text-warning"></i>
        <div>
          <strong>Aguardando decisão:</strong>
          Clique em "Adicionar PO" no grid acima para decidir se deseja pré-preencher os dados com as informações do contrato.
        </div>
      </div>

      <!-- ============================================================ -->
      <!-- FRAME FIXO SUPERIOR: Grid de POs do contrato (colapsável)     -->
      <!-- ============================================================ -->
      <div v-if="modoContratoCompra && idContratoEfetivo" class="card mb-4 border-info-subtle shadow-sm">
        <div class="card-header grid-po-card-header d-flex justify-content-between align-items-center cursor-pointer" @click="togglePurchaseOrdersGrid">
          <h6 class="mb-0 text-info-emphasis fw-bold d-flex align-items-center">
            <i class="bi bi-grid-3x3-gap me-2"></i>Purchase Orders do Contrato: {{ item.numeroContratoFornecedor || contextoPai?.numeroContratoFornecedor }}
            <span v-if="purchaseOrdersContrato.length > 0" class="ms-3 fw-normal fs-6 badge bg-info bg-opacity-10 text-info-emphasis border border-info-subtle">
              {{ purchaseOrdersContrato.length }} {{ purchaseOrdersContrato.length === 1 ? 'PO cadastrada' : 'POs cadastradas' }}
            </span>
          </h6>
          <div class="d-flex align-items-center gap-2">
            <button
              v-if="temPurchaseOrdersCompleto"
              type="button"
              class="btn btn-sm btn-info text-white"
              @click.stop="iniciarNovaPurchaseOrder"
            >
              <i class="bi bi-plus-lg"></i> Adicionar PO
            </button>
            <i class="bi fs-5" :class="purchaseOrdersGridAberto ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          </div>
        </div>
        <div v-show="purchaseOrdersGridAberto" class="card-body p-0">
          <div class="grid-scroll-container">
            <div v-if="isLoadingPurchaseOrders" class="text-center py-4">
              <div class="spinner-border spinner-border-sm text-info me-2"></div>
              Carregando purchase orders...
            </div>
            <table v-else class="table table-hover admin-table mb-0">
              <thead>
                <tr>
                  <th style="width: 80px;">ID</th>
                  <th>Nº PO</th>
                  <th>Ref.</th>
                  <th>Emissão</th>
                  <th>Status</th>
                  <th class="text-end">Valor Original</th>
                  <th class="text-end">Valor Negociado</th>
                  <th class="text-center" style="width: 80px;">Ativa</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="po in purchaseOrdersContratoOrdenadas"
                  :key="po.idPurchaseOrder"
                  @click="temVisualizacao && selecionarPurchaseOrder(po)"
                  class="cursor-pointer"
                  :class="{'linha-selecionada border-start border-4 border-info': po.idPurchaseOrder === item.idPurchaseOrder}"
                >
                  <td><code>#{{ po.idPurchaseOrder }}</code></td>
                  <td><code>{{ po.numeroPurchaseOrder || po.nomeReferencia }}</code></td>
                  <td><code>{{ po.nomeReferencia }}</code></td>
                  <td>{{ po.dataEmissao ? new Date(po.dataEmissao).toLocaleDateString() : '-' }}</td>
                  <td>
                    <span :class="['badge', obterCorStatus(getTextoLista(listas.LstStatusPurchaseOrder, po.lstStatusPurchaseOrder))]">
                      {{ getTextoLista(listas.LstStatusPurchaseOrder, po.lstStatusPurchaseOrder) }}
                    </span>
                  </td>
                  <td class="text-end fw-bold">{{ formatarDecimal(po.valorTotalOriginal) }}</td>
                  <td class="text-end fw-bold">{{ formatarDecimal(po.valorTotalNegociado) }}</td>
                  <td class="text-center">
                    <i v-if="po.idPurchaseOrder === item.idPurchaseOrder" class="bi bi-check-circle-fill text-info fs-5"></i>
                  </td>
                </tr>
                <tr v-if="purchaseOrdersContrato.length === 0">
                  <td colspan="8" class="text-center py-4 text-muted">Nenhuma purchase order encontrada.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div v-if="!podeEditar" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
        <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
        <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registro.</div>
      </div>

      <!-- ============================================================ -->
      <!-- BARRA DE AÇÕES DA PURCHASE ORDER                              -->
      <!-- Organização: Cancelar (esquerda) | Funções | Workflow-forward -->
      <!-- ============================================================ -->
      <div v-if="isPurchaseOrderSaved" class="d-flex justify-content-between align-items-center gap-2 mb-3">
        <div>
          <!-- CANCELAR: sozinho à esquerda (seq < EmbarcadaParcial) -->
          <button
            v-if="podeCancelarPO"
            type="button"
            class="btn btn-sm btn-outline-danger fw-bold"
            :disabled="isLoadingAcao"
            @click="cancelarPO"
          >
            <i class="bi bi-x-lg me-1"></i>
            Cancelar PO
          </button>
        </div>
        <div class="d-flex align-items-center gap-2">
          <!-- FUNÇÃO: Relatório Gerencial (sempre que salva) -->
          <button
            v-if="isPurchaseOrderSaved"
            type="button"
            class="btn btn-sm btn-outline-secondary fw-bold"
            @click="gerarRelatorioGerencial"
          >
            <i class="bi bi-bar-chart-line me-1"></i>
            Relatório Gerencial
          </button>
          <!-- FUNÇÃO: Relatório Fornecedor (visível a partir de Emitida, seq >= PO_ST9) -->
          <button
            v-if="isPurchaseOrderSaved && (PO_ST.sequencia[item.lstStatusPurchaseOrder] || 0) >= (PO_ST.sequencia[PO_ST.Emitida] || 0)"
            type="button"
            class="btn btn-sm btn-outline-primary fw-bold"
            @click="gerarRelatorioFornecedorIngles"
          >
            <i class="bi bi-translate me-1"></i>
            Relatório Fornecedor
          </button>
          <!-- WORKFLOW: Emitir PO (visível quando Aprovada) -->
          <button
            v-if="podeEmitirPO"
            type="button"
            class="btn btn-sm btn-success fw-bold"
            :disabled="isLoadingAcao"
            @click="emitirPO"
          >
            <span v-if="isLoadingAcao" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-send-check me-1"></i>
            Emitir PO
          </button>
          <!-- WORKFLOW: Solicitar Aprovação (visível quando EmDigitacao) -->
          <button
            v-if="podeSolicitarAprovacao"
            type="button"
            class="btn btn-sm btn-outline-primary fw-bold"
            :disabled="isLoadingAcao"
            @click="solicitarAprovacao"
          >
            <span v-if="isLoadingAcao" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-send-check me-1"></i>
            Solicitar Aprovação
          </button>
          <!-- WORKFLOW: Encerrar PO (visível quando EmbarcadaParcial) -->
          <button
            v-if="podeEncerrarPO"
            type="button"
            class="btn btn-sm btn-outline-secondary fw-bold"
            :disabled="isLoadingAcao"
            @click="encerrarPO"
          >
            <span v-if="isLoadingAcao" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-check-all me-1"></i>
            Encerrar PO
          </button>
          <!-- WORKFLOW: Encerrar Parcialmente (visível quando FaturadaParcialmente) -->
          <button
            v-if="podeEncerrarParcialmente"
            type="button"
            class="btn btn-sm btn-outline-warning fw-bold"
            :disabled="isLoadingAcao"
            @click="encerrarPO"
          >
            <span v-if="isLoadingAcao" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-check-all me-1"></i>
            Encerrar Parcialmente
          </button>
          <!-- WORKFLOW: Retornar PO a Pendente (visível quando Reprovada) -->
          <button
            v-if="podeRetornarPendente"
            type="button"
            class="btn btn-sm btn-outline-warning fw-bold"
            :disabled="isLoadingAcao"
            @click="retornarPendente"
          >
            <span v-if="isLoadingAcao" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-arrow-return me-1"></i>
            Retornar PO a Pendente
          </button>
        </div>
      </div>

      <!-- ============================================================ -->
      <!-- FRAME: DADOS BÁSICOS (campos obrigatórios do modelo)          -->
      <!-- ============================================================ -->
      <div class="card mb-4 border-info-subtle shadow-sm" :class="{'frame-bloqueado': frameBasicosBloqueado}">
        <div class="card-header bg-info-subtle py-2 cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.dadosBasicos = !accordionState.dadosBasicos">
          <h6 class="mb-0 text-info-emphasis fw-bold"><i class="bi bi-file-earmark-text me-2"></i>Dados Básicos</h6>
          <i class="bi fs-5" :class="accordionState.dadosBasicos ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
        <div class="card-body" v-show="accordionState.dadosBasicos">
          <div class="row g-3">
            <div class="col-md-4">
              <CampoBusca
                id="idFilial"
                label="Filial"
                v-model="item.idFilial"
                :configuracaoBusca="configBuscaFilial"
                required
                :disabled="campoDesabilitado"
              />
            </div>
            <div class="col-md-4">
              <CampoBusca
                id="idParceiroNegocioFornecedor"
                label="Fornecedor"
                v-model="item.idParceiroNegocioFornecedor"
                :configuracaoBusca="configBuscaFornecedor"
                required
                :disabled="campoDesabilitado"
              />
            </div>
            <div class="col-md-4">
              <CampoBusca
                id="idOperacao"
                label="Operação"
                v-model="item.idOperacao"
                :configuracaoBusca="criarConfigBuscaOperacaoCompra()"
                required
                :disabled="campoDesabilitado"
              />
            </div>
            <div class="col-md-4">
              <CampoInput
                id="nomeReferencia"
                label="Nome de Referência"
                v-model="item.nomeReferencia"
                type="text"
                required
                maxlength="100"
                :disabled="campoDesabilitado"
              />
            </div>
            <div class="col-md-4">
              <CampoInput
                id="numeroPurchaseOrder"
                label="Nº Purchase Order"
                v-model="item.numeroPurchaseOrder"
                type="text"
                maxlength="50"
                :disabled="true"
              />
              <small class="text-muted d-block mt-1">
                <i class="bi bi-robot me-1"></i>Gerado automaticamente pelo sistema.
              </small>
            </div>
            <div class="col-md-4">
              <CampoBuscaLista
                id="lstMoeda"
                label="Moeda da Operação"
                v-model="item.lstMoeda"
                tabela="PurchaseOrder"
                required
                :disabled="campoDesabilitado"
              />
            </div>
            <div class="col-md-4">
              <CampoSelect
                id="lstStatusPurchaseOrder"
                label="Status"
                v-model="item.lstStatusPurchaseOrder"
                :options="listas.LstStatusPurchaseOrder || []"
                option-label="textoLista"
                option-value="idListaClienteItem"
                :disabled="true"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- ============================================================ -->
      <!-- BOTÃO SALVAR E CONTINUAR                                      -->
      <!-- ============================================================ -->
      <div class="mb-4 text-center" v-if="!isPurchaseOrderSaved && temPurchaseOrdersCompleto && !modoEspera">
        <button
          type="button"
          @click.prevent="salvarEContinuar"
          class="btn btn-info text-white px-4 py-2 fw-bold"
          :disabled="isLoading"
        >
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
          <i v-if="!isLoading" class="bi bi-chevron-double-down me-1"></i>
          Salvar e Continuar
          <i v-if="!isLoading" class="bi bi-chevron-double-down ms-1"></i>
        </button>
        <div class="form-text mt-2 text-info">
          <i class="bi bi-info-circle me-1"></i> Salve os dados básicos para habilitar o cadastro de Produtos e Embarques.
        </div>
      </div>

      <!-- ============================================================ -->
      <!-- ABAS: Operação, Comercial, Pricing, Câmbio                    -->
      <!-- ============================================================ -->
      <fieldset class="mb-3">
        <div class="card border-info-subtle shadow-sm" :class="{'frame-bloqueado': abasBloqueadas}">
          <div class="card-header bg-info-subtle py-0 px-0 border-bottom-0 cursor-pointer d-flex justify-content-between align-items-start" @click="toggleAbasHeader">
            <ul class="nav nav-tabs mb-0 px-3 pt-2 flex-grow-1" role="tablist">
              <li class="nav-item" role="presentation">
                <button class="nav-link" :class="{ active: abaAtiva === 'operacao' }" @click="abaAtiva = 'operacao'" type="button" role="tab">
                  <i class="bi bi-info-circle me-1"></i> Dados da Operação
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button class="nav-link" :class="{ active: abaAtiva === 'comercial' }" @click="abaAtiva = 'comercial'" type="button" role="tab">
                  <i class="bi bi-briefcase me-1"></i> Comercial
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button class="nav-link" :class="{ active: abaAtiva === 'pricing' }" @click="abaAtiva = 'pricing'" type="button" role="tab">
                  <i class="bi bi-currency-dollar me-1"></i> Pricing & Valores
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button class="nav-link" :class="{ active: abaAtiva === 'cambio' }" @click="abaAtiva = 'cambio'" type="button" role="tab">
                  <i class="bi bi-currency-exchange me-1"></i> Câmbio
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button class="nav-link" :class="{ active: abaAtiva === 'aprovacao' }" @click="abaAtiva = 'aprovacao'" type="button" role="tab">
                  <i class="bi bi-person-check me-1"></i> Aprovação
                </button>
              </li>
            </ul>
            <i class="bi fs-5 pt-2 pe-3 flex-shrink-0" :class="accordionState.abas ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          </div>
          <div class="card-body" v-show="accordionState.abas">
            <!-- TAB: Dados da Operação -->
            <div v-show="abaAtiva === 'operacao'" role="tabpanel">
              <div class="row g-3">
                <!-- Linha: Local Estoque Previsto + Observações (sem frame) -->
                <div class="col-md-3">
                  <CampoBusca
                    id="idLocalEstoquePrevisto"
                    label="Local Estoque Previsto"
                    v-model="item.idLocalEstoquePrevisto"
                    :configuracaoBusca="configBuscaLocalEstoque"
                    :disabled="campoDesabilitado"
                  />
                </div>
                <div class="col-md-9">
                  <CampoInput
                    id="observacoes"
                    label="Observações"
                    v-model="item.observacoes"
                    type="textarea"
                    :disabled="campoDesabilitadoPosAprovacao"
                  />
                </div>

                <!-- Card: Datas do Cronograma -->
                <div class="col-12">
                  <div class="card border-info-subtle mb-3">
                    <div class="card-header bg-info-subtle py-2">
                      <h6 class="mb-0 text-info-emphasis fw-bold"><i class="bi bi-calendar-event me-2"></i>Datas do Cronograma</h6>
                    </div>
                    <div class="card-body">
                      <div class="row g-3">
                        <div class="col-md-3">
                          <CampoInput
                            id="dataEmissao"
                            label="Data de Emissão"
                            v-model="item.dataEmissao"
                            type="date"
                            disabled
                          />
                        </div>
                        <div class="col-md-3">
                          <CampoInput
                            id="previsaoProducao"
                            label="Previsão de Produção"
                            v-model="item.previsaoProducao"
                            type="date"
                            :disabled="campoDesabilitado"
                          />
                        </div>
                        <div class="col-md-3">
                          <CampoInput
                            id="previsaoEmbarqueETD"
                            label="Previsão de Embarque (ETD)"
                            v-model="item.previsaoEmbarqueETD"
                            type="date"
                            :disabled="campoDesabilitado"
                          />
                        </div>
                        <div class="col-md-3">
                          <CampoInput
                            id="previsaoEmbarqueETA"
                            label="Previsão de Chegada (ETA)"
                            v-model="item.previsaoEmbarqueETA"
                            type="date"
                            :disabled="campoDesabilitado"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- TAB: Comercial -->
            <div v-show="abaAtiva === 'comercial'" role="tabpanel">
              <div class="row g-3">
                <!-- Campos comerciais sem frame -->
                <div class="col-md-4">
                  <CampoBuscaLista
                    id="lstIncoterm"
                    label="Incoterm"
                    v-model="item.lstIncoterm"
                    tabela="PurchaseOrder"
                    :disabled="campoDesabilitado"
                  />
                </div>
                <div class="col-md-4">
                  <CampoBuscaLista
                    id="lstLocalIncoterm"
                    label="Local Incoterm"
                    v-model="item.lstLocalIncoterm"
                    tabela="PurchaseOrder"
                    :disabled="campoDesabilitado"
                  />
                </div>
                <div class="col-md-4">
                  <CampoInput
                    id="numeroProformaFornecedor"
                    label="Nº Proforma Fornecedor"
                    v-model="item.numeroProformaFornecedor"
                    type="text"
                    maxlength="50"
                    :disabled="campoDesabilitadoPosAprovacao"
                  />
                </div>
                <div class="col-md-6">
                  <CampoBuscaLista
                    id="lstFormaPagamento"
                    label="Forma de Pagamento"
                    v-model="item.lstFormaPagamento"
                    tabela="PurchaseOrder"
                    :disabled="campoDesabilitado"
                  />
                </div>
                <div class="col-md-3">
                  <CampoInput
                    id="percentualTolerancia"
                    label="% Tolerância"
                    v-model="item.percentualTolerancia"
                    type="number"
                    step="0.01"
                    required
                    :disabled="campoDesabilitado"
                  />
                </div>

                <!-- Card: Condições de Pagamento (grid) -->
                <div class="col-12">
                  <template v-if="isPurchaseOrderSaved">
                    <div class="card border-info-subtle">
                      <div class="card-header bg-info-subtle d-flex justify-content-between align-items-center">
                        <h5 class="mb-0 text-info-emphasis fw-bold">
                          <i class="bi bi-credit-card me-2"></i>Condições de Pagamento
                          <span
                            v-if="condicoesPagamento.length > 0"
                            class="ms-3 fw-normal fs-6 badge bg-info bg-opacity-10 text-info-emphasis border border-info-subtle"
                          >
                            {{ condicoesPagamento.length }} {{ condicoesPagamento.length === 1 ? 'parcela' : 'parcelas' }}
                          </span>
                        </h5>
                        <div class="d-flex align-items-center gap-2">
                          <span
                            class="badge fw-normal px-3 py-2"
                            :class="somaExcedidaCondicoes ? 'bg-danger bg-opacity-10 text-danger-emphasis border border-danger-subtle' : 'bg-success bg-opacity-10 text-success-emphasis border border-success-subtle'"
                          >
                            <i class="bi bi-pie-chart me-1"></i>
                            {{ somaPercentuaisCondicoes.toFixed(2) }}% alocado
                            <span v-if="!somaExcedidaCondicoes"> / {{ percentualRestanteCondicoes.toFixed(2) }}% restante</span>
                          </span>
                          <button
                            v-if="!condicoesPagamentoBloqueadas"
                            type="button"
                            class="btn btn-sm btn-info text-white"
                            @click="iniciarNovaCondicao"
                          >
                            <i class="bi bi-plus-lg"></i> Adicionar Condição
                          </button>
                        </div>
                      </div>
                      <div class="card-body p-0">
                        <div class="grid-scroll-container">
                          <div v-if="isLoadingCondicoes" class="text-center py-4">
                            <div class="spinner-border spinner-border-sm text-info me-2"></div>
                            Carregando condições de pagamento...
                          </div>
                          <table v-else class="table admin-table mb-0">
                            <thead>
                              <tr>
                                <th>Descrição</th>
                                <th v-if="!condicoesPagamentoBloqueadas" style="width: 100px;">Ações</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="cond in condicoesPagamento" :key="cond.idPurchaseOrderCondicaoPg">
                                <td>{{ gerarDescricaoCondicao(cond) }}</td>
                                <td v-if="!condicoesPagamentoBloqueadas" class="text-end pe-3">
                                  <button type="button" class="btn-icon" @click="editarCondicao(cond)" title="Editar">
                                    <i class="bi bi-pencil-fill"></i>
                                  </button>
                                  <button type="button" class="btn-icon text-danger" @click="solicitarExclusaoCondicao(cond)" title="Remover">
                                    <i class="bi bi-trash-fill"></i>
                                  </button>
                                </td>
                              </tr>
                              <tr v-if="condicoesPagamento.length === 0 && !isLoadingCondicoes">
                                <td :colspan="condicoesPagamentoBloqueadas ? 1 : 2" class="text-center py-4 text-muted">
                                  <i class="bi bi-info-circle me-2"></i>
                                  Nenhuma condição de pagamento cadastrada.
                                  <span v-if="!condicoesPagamentoBloqueadas">Clique em "Adicionar Condição" para começar.</span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <ConfirmationModal
                        ref="modalExclusaoCondicaoRef"
                        title="Remover Condição de Pagamento"
                        message="Tem certeza que deseja remover esta condição de pagamento? Esta ação não pode ser desfeita."
                        @confirmed="confirmarExclusaoCondicao"
                      />
                    </div>
                  </template>
                  <div v-else class="text-center py-3 text-muted">
                    <i class="bi bi-info-circle me-2"></i> Salve a purchase order primeiro para gerenciar condições de pagamento.
                  </div>
                </div>
              </div>
            </div>

            <!-- TAB: Pricing & Valores -->
            <div v-show="abaAtiva === 'pricing'" role="tabpanel">
              <div class="row g-3">
                <!-- Card Único: Template e Atributos de Precificação -->
                <div class="col-12">
                  <div class="card border-info-subtle mb-3">
                    <div class="card-header bg-info-subtle py-2">
                      <h6 class="mb-0 text-info-emphasis fw-bold"><i class="bi bi-layout-three-columns me-2"></i>Template e Atributos de Precificação</h6>
                    </div>
                    <div class="card-body">
                      <div class="row g-3">
                        <div class="col-md-6">
                          <CampoBusca
                            id="idTemplatePrecificacao"
                            label="Template de Precificação"
                            v-model="item.idTemplatePrecificacao"
                            :configuracaoBusca="configBuscaTemplate"
                            :disabled="campoDesabilitado"
                            @selecionado="onTemplateSelecionado"
                          />
                        </div>
                      </div>

                      <!-- Divisor entre template e atributos (só aparece quando há template) -->
                      <div v-if="pricingFields.length > 0">
                        <hr class="my-3">
                        <div class="row g-3">
                          <div v-for="campo in pricingFields" :key="campo.chave" class="col-md-4">
                            <CampoInput
                              :id="campo.chave"
                              :label="campo.label || ' '"
                              v-model="valoresPricing[campo.chave]"
                              :disabled="campoDesabilitado"
                            />
                          </div>
                        </div>
                      </div>

                      <div v-if="pricingFields.length === 0" class="text-center py-3 text-muted">
                        <i class="bi bi-info-circle me-2"></i>Selecione um template de precificação para habilitar os campos de atributos dinâmicos.
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Card: Breakdown de Valores -->
                <div class="col-12">
                  <div class="card border-info-subtle mb-3">
                    <div class="card-header bg-info-subtle py-2">
                      <h6 class="mb-0 text-info-emphasis fw-bold"><i class="bi bi-calculator me-2"></i>Breakdown de Valores</h6>
                    </div>
                    <div class="card-body">
                      <div class="row g-3">
                        <div class="col-md-3">
                          <CampoInput
                            id="valorTotalOriginal"
                            :label="'Valor Original (' + getTextoMoeda() + ')'"
                            :model-value="valorTotalCalculado"
                            type="moeda"
                            readonly
                            :disabled="true"
                          />
                        </div>
                        <div class="col-md-3">
                          <CampoInput
                            id="valorTotalFrete"
                            :label="'Valor Frete (' + getTextoMoeda() + ')'"
                            v-model="item.valorTotalFrete"
                            type="moeda"
                            :disabled="campoDesabilitado"
                            :class="{'is-warning': item.valorTotalFrete > valorTotalCalculado * 0.1}"
                          />
                          <small v-if="item.valorTotalFrete > valorTotalCalculado * 0.1" class="text-warning d-block mt-1">
                            <i class="bi bi-exclamation-triangle me-1"></i>Frete elevado (acima de 10% do valor original)
                          </small>
                        </div>
                        <div class="col-md-3">
                          <CampoInput
                            id="valorTotalDesconto"
                            :label="'Valor Desconto (' + getTextoMoeda() + ')'"
                            :model-value="item.valorTotalDesconto"
                            type="moeda"
                            readonly
                            :disabled="true"
                          />
                        </div>
                        <div class="col-md-3">
                          <CampoInput
                            id="valorTotalNegociado"
                            :label="'Valor Negociado (' + getTextoMoeda() + ')'"
                            :model-value="(Number(valorTotalCalculado) || 0) + (Number(item.valorTotalFrete) || 0) - (Number(item.valorTotalDesconto) || 0)"
                            type="moeda"
                            readonly
                            :disabled="true"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- TAB: Câmbio e Conversão (espelhando TítulosFinanceiros) -->
            <div v-show="abaAtiva === 'cambio'" role="tabpanel" v-if="isEditing">
              <div class="row g-3">
                <!-- Linha de metadados (igual ao título) -->
                <div class="col-12">
                  <div class="row g-3 mb-4 pb-3 border-bottom">
                    <div class="col-md-3">
                      <CampoBuscaLista id="lstTipoTaxaConversao" label="Tipo Taxa Conversão"
                        v-model="item.lstTipoTaxaConversao" tabela="PurchaseOrder"
                        :disabled="true"
                        descricao-informativa="Campos de conversão são gerados automaticamente" />
                    </div>
                    <div class="col-md-2">
                      <CampoInput id="dataReferenciaCambio" label="Data Ref. Câmbio" type="date"
                        v-model="item.dataReferenciaCambio" :disabled="true"
                        descricao-informativa="Campos de conversão são gerados automaticamente" />
                    </div>
                    <div class="col-md-3">
                      <CampoBuscaLista id="lstFonteCambio" label="Fonte da Cotação"
                        v-model="item.lstFonteCambio" tabela="PurchaseOrder"
                        :disabled="true"
                        descricao-informativa="Campos de conversão são gerados automaticamente" />
                    </div>
                    <div class="col-md-4">
                      <CampoBuscaLista id="lstTaxaCambioStatus" label="Status Taxa"
                        v-model="item.lstTaxaCambioStatus" tabela="PurchaseOrder"
                        :disabled="true"
                        descricao-informativa="Campos de conversão são gerados automaticamente" />
                    </div>
                  </div>
                </div>

                <!-- Mini-cards: Moeda Padrão vs Moeda Gerencial -->
                <div class="col-md-6">
                  <div class="card border-primary h-100">
                    <div class="card-header bg-primary text-white py-2 d-flex justify-content-between align-items-center">
                      <h6 class="mb-0 text-white small"><i class="bi bi-currency-dollar me-1"></i> Moeda Padrão</h6>
                      <div class="d-flex gap-1">
                        <button v-if="isPurchaseOrderSaved && item.lstFonteCambio === MOE_FONTE_COTACAO.Manual"
                          type="button" class="btn btn-sm btn-outline-light py-0"
                          :disabled="isResolvendoTaxas" @click.stop="resolverTaxasAutomaticamente"
                          title="Buscar cotações automaticamente">
                          <i class="bi bi-robot me-1"></i> Definir Automaticamente
                        </button>
                        <button v-if="isPurchaseOrderSaved"
                          type="button" class="btn btn-sm btn-light py-0"
                          @click.stop="abrirModalCotacaoManual"
                          title="Definir manualmente as taxas de câmbio">
                          <i class="bi bi-pencil-square me-1"></i> Definir Manualmente
                        </button>
                      </div>
                    </div>
                    <div class="card-body py-2">
                      <div class="row g-2">
                        <div class="col-4">
                          <CampoBuscaLista id="lstMoedaPadrao" label="Moeda"
                            v-model="item.lstMoedaPadrao" tabela="PurchaseOrder" :disabled="true"
                            descricao-informativa="Campos de conversão são gerados automaticamente" />
                        </div>
                        <div class="col-4">
                          <CampoInput id="taxaCambioPadrao" :label="labelTaxaPadrao"
                            type="moeda" :casasDecimais="6"
                            :model-value="item.taxaCambioPadrao" disabled />
                        </div>
                        <div class="col-4">
                          <CampoInput id="valorTotalNegociadoPadrao" label="Valor Negociado"
                            type="moeda"
                            :model-value="item.valorTotalNegociadoPadrao" disabled />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="card border-primary h-100">
                    <div class="card-header bg-primary text-white py-2">
                      <h6 class="mb-0 text-white small"><i class="bi bi-currency-dollar me-1"></i> Moeda Gerencial</h6>
                    </div>
                    <div class="card-body py-2">
                      <div class="row g-2">
                        <div class="col-3">
                          <CampoBuscaLista id="lstMoedaGerencial" label="Moeda"
                            v-model="item.lstMoedaGerencial" tabela="PurchaseOrder" :disabled="true"
                            descricao-informativa="Campos de conversão são gerados automaticamente" />
                        </div>
                        <div class="col-3">
                          <CampoInput id="taxaCambioGerencialPadrao" :label="labelTaxaGerencialPadrao"
                            type="moeda" :casasDecimais="6"
                            :model-value="item.taxaCambioGerencialPadrao" disabled />
                        </div>
                        <div class="col-3">
                          <CampoInput id="taxaCambioGerencial" :label="labelTaxaCambioGerencial"
                            type="moeda" :casasDecimais="6"
                            :model-value="item.taxaCambioGerencial" disabled />
                        </div>
                        <div class="col-3">
                          <CampoInput id="valorTotalNegociadoGerencial" label="Valor Negociado"
                            type="moeda"
                            :model-value="item.valorTotalNegociadoGerencial" disabled />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Tabela de Breakdown (4 valores em 3 moedas) -->
                <div class="col-12">
                  <div class="card border-secondary mt-3">
                    <div class="card-header bg-body-tertiary py-2">
                      <h6 class="mb-0"><i class="bi bi-table me-1 text-primary"></i> Valores Convertidos (Breakdown)</h6>
                    </div>
                    <div class="card-body p-0">
                      <div class="table-responsive">
                        <table class="table table-sm table-bordered admin-table mb-0">
                          <thead>
                            <tr>
                              <th style="width: 120px;">Breakdown</th>
                              <th class="text-end">Moeda Original ({{ obterSiglaMoeda(item.lstMoeda) || '-' }})</th>
                              <th class="text-end">Moeda Padrão ({{ obterSiglaMoeda(item.lstMoedaPadrao) || '-' }})</th>
                              <th class="text-end">Moeda Gerencial ({{ obterSiglaMoeda(item.lstMoedaGerencial) || '-' }})</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td><strong>Valor Original</strong></td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalOriginal) }}</td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalOriginalPadrao) }}</td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalOriginalGerencial) }}</td>
                            </tr>
                            <tr>
                              <td><strong>Frete</strong></td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalFrete) }}</td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalFretePadrao) }}</td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalFreteGerencial) }}</td>
                            </tr>
                            <tr>
                              <td><strong>Desconto</strong></td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalDesconto) }}</td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalDescontoPadrao) }}</td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalDescontoGerencial) }}</td>
                            </tr>
                            <tr class="table-active fw-bold">
                              <td><strong>Negociado</strong></td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalNegociado) }}</td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalNegociadoPadrao) }}</td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalNegociadoGerencial) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- TAB: Aprovação -->
            <div v-show="abaAtiva === 'aprovacao'" role="tabpanel"
                 :class="errors.resumoAprovacao || errors.idGrupoAcessoAprovador ? 'border border-danger p-2 rounded-2' : ''">
              <!-- Observação de reprovação (backward-compat: POs reprovadas no fluxo legado) -->
              <div v-if="item.observacaoReprovacao" class="alert alert-danger border-0 rounded-3 d-flex align-items-start gap-2 mb-3 py-2">
                <i class="bi bi-x-octagon-fill fs-5 flex-shrink-0 mt-1"></i>
                <div>
                  <strong class="d-block small">Motivo da Reprovação</strong>
                  <span class="small">{{ item.observacaoReprovacao }}</span>
                </div>
              </div>
              <WorkflowAprovacaoGrid
                ref="workflowGridRef"
                sem-frame
                entidade="PurchaseOrder"
                :id-entidade="item.idPurchaseOrder"
                :processos="processosAprovacao"
                :pode-solicitar="podeSolicitarAprovacao"
                modo="parent-driven"
                modulo-regra-acesso="APROVACAO_PO"
                @solicitacao-preenchida="handleSolicitacaoPreenchida"
                @show-notification="(p) => emit('show-notification', p)"
              />
            </div>
          </div>
        </div>
      </fieldset>

        <!-- ============================================================ -->
        <!-- FRAME: Produtos da PO                                          -->
        <!-- ============================================================ -->
        <fieldset :disabled="framesSecundariosBloqueados" class="mb-3">
          <div class="card mb-3 border-info-subtle" :class="{'frame-bloqueado': framesSecundariosBloqueados}">
            <div class="card-header bg-info-subtle py-2 cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.produtos = !accordionState.produtos">
              <h6 class="mb-0 text-info-emphasis fw-bold">
                <i class="bi bi-box-seam me-2"></i>Produtos da Purchase Order
                <span v-if="produtosPurchaseOrder.length > 0" class="ms-2 fw-normal fs-6 badge bg-info bg-opacity-10 text-info-emphasis border border-info-subtle">
                  {{ produtosPurchaseOrder.length }} {{ produtosPurchaseOrder.length === 1 ? 'produto' : 'produtos' }}
                </span>
                <span v-if="isPurchaseOrderSaved && produtosPurchaseOrder.length > 0" class="ms-2 fw-normal fs-6 badge" :class="percentualPendenteDocs > 0 ? 'bg-warning bg-opacity-10 text-warning-emphasis border border-warning-subtle' : 'bg-success bg-opacity-10 text-success-emphasis border border-success-subtle'">
                  {{ percentualPendenteDocs }}% pendente de alocação em Docs de Entrada
                </span>
              </h6>
              <i class="bi fs-5" :class="accordionState.produtos ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
            <div class="card-body p-0 border-top border-info-subtle" v-show="accordionState.produtos">
            <div class="card-body p-0 border-top border-info-subtle">
              <div v-if="isLoadingProdutosPurchaseOrder" class="text-center py-4">
                <div class="spinner-border spinner-border-sm text-info me-2"></div>
                Carregando produtos...
              </div>
              <div v-show="!isLoadingProdutosPurchaseOrder" class="p-3">
                <PurchaseOrderSmartMode
                  v-if="isPurchaseOrderSaved"
                  :key="item.idPurchaseOrder"
                  :id-purchase-order="item.idPurchaseOrder"
                  :produtos-com-saldo="produtosComSaldoPendente"
                  :produtos-purchase-order="produtosPurchaseOrder"
                  :listas="listas"
                  :pode-editar="podeEditar"
                  :is-pos-aprovacao="isPosAprovacao"
                  :accordion-fechado="false"
                  :percentual-tolerancia-padrao="item.percentualTolerancia"
                  :lst-moeda="item.lstMoeda"
                  :modo-alocacao="modoAlocacaoSmartMode"
                  :id-parceiro-negocio-fornecedor="item.idParceiroNegocioFornecedor"
                  @alocar-produto="handleAlocarProdutoSmartMode"
                  @remover-produto="handleRemoverProdutoSmartMode"
                  @cancelar-produto="handleCancelarProdutoSmartMode"
                  @show-notification="(p) => emit('show-notification', p)"
                />
                <div v-if="!isPurchaseOrderSaved" class="text-center py-4 text-muted">
                  <i class="bi bi-info-circle me-2"></i> Salve a purchase order primeiro para gerenciar produtos.
                </div>
              </div>
            </div>
          </div>
        </div>
        </fieldset>

        <!-- ============================================================ -->
        <!-- Cláusulas e Termos (card próprio no componente)                -->
        <!-- ============================================================ -->
        <div v-if="isPurchaseOrderSaved" class="mb-3">
          <ClausulasGrid
            ref="clausulasGridRef"
            :id-documento="item.idPurchaseOrder"
            tipo-documento="PO"
            :lst-aplicacao-clausula="CLAUSULAS_COM_APLIC.PO"
            :pode-editar="podeEditar && !isPosAprovacao"
            :ids-produtos="produtosPurchaseOrder.map(p => ({ idProduto: p.idProduto, sku: p.sku, nomeProduto: p.nomeProduto }))"
            :ids-parceiros="item.idParceiroNegocioFornecedor ? [{ idParceiro: item.idParceiroNegocioFornecedor, nomeParceiro: nomeFornecedor }] : []"
            :com-accordion="true"
            :accordion-aberto="accordionState.clausulas"
            :clausulas-inicializadas="item.clausulasInicializadas"
            @update:accordion-aberto="accordionState.clausulas = $event"
            @show-notification="(p) => emit('show-notification', p)"
          />
        </div>
        <div v-else class="text-center py-4 text-muted mb-3">
          <i class="bi bi-info-circle me-2"></i> Salve a purchase order primeiro para gerenciar cláusulas.
        </div>

        <!-- ============================================================ -->
        <!-- Documentos de Entrada (card próprio no componente)             -->
        <!-- ============================================================ -->
        <div v-if="!framesSecundariosBloqueados && isLiberadaParaDocumentos" class="mb-3">
          <DocumentoEntradaGrid
            ref="documentoEntradaGridRef"
            :id-purchase-order="item.idPurchaseOrder"
            :tem-modulo-documento="temDocumentoEntrada"
            :tem-completo="temPurchaseOrdersCompleto"
            :tem-visualizacao="temVisualizacao"
            :pode-editar="podeEditar && !isTerminal"
            :listas="listas"
            :id-filial="item.idFilial"
            :lst-moeda="item.lstMoeda || ''"
            :id-operacao="item.idOperacao"
            :id-parceiro-negocio-emissor="item.idParceiroNegocioFornecedor || null"
            :is-interno="false"
            :id-organizacao="authStore.empresaAtiva"
            :lst-tipo-taxa-conversao="FIN_TP_TAX_CONVERT.PTAXDataEmissaoDocumentoEntrada"
            :habilitar-parceiro-produtor="true"
            :com-smart-mode="true"
            :config-busca-lote-fabricacao="configBuscaLoteFabricacao"
            :id-parceiro-negocio-fornecedor="item.idParceiroNegocioFornecedor || null"
            :lst-tipos-local-estoque="[ESTQ_TIPO_LOCAL_ESTOCAGEM.PreEmbarque]"
            contexto-origem="purchaseOrder"
            modo-alocacao="saldo"
            :com-accordion="true"
            :accordion-aberto="accordionState.documentosEntrada"
            @update:accordion-aberto="accordionState.documentosEntrada = $event"
            @acao-completa="onAcaoDocumentoCompleta"
            @show-notification="(p) => emit('show-notification', p)"
          />
        </div>
        <div v-else-if="!framesSecundariosBloqueados" class="text-center py-4 text-muted mb-3">
          <i class="bi bi-info-circle me-2"></i> Salve a purchase order primeiro para gerenciar documentos de entrada.
        </div>

      <!-- AUDITORIA -->
      <AuditLogViewer
        v-if="mostrarAuditoria"
        tabela="PurchaseOrder"
        :chave-primaria="item.idPurchaseOrder"
        :titulo-registro="item.nomeReferencia"
        :dicionarios="{ LstStatusPurchaseOrder: listas.LstStatusPurchaseOrder || [] }"
        contexto="tenant"
        :tabelas-filhas="['PurchaseOrderProduto', 'PurchaseOrderCondicaoPg', 'PurchaseOrderClausula']"
        chave-estrangeira="IdPurchaseOrder"
        @fechar="mostrarAuditoria = false"
      />

    </FormularioCadastro>
    </template>

    <!-- MODAL DO RELATÓRIO GERENCIAL (Paged.js — uso interno) -->
    <RelatorioPreviewModal
      :templates="templatesRelatorioGerencial"
      :dados-relatorio="dadosRelatorioGerencial"
      :visible="showRelatorioGerencialModal"
      :id-empresa="Number(authStore.empresaAtiva)"
      @fechar="showRelatorioGerencialModal = false"
      @show-notification="(p) => emit('show-notification', p)"
    />

    <!-- MODAL DO RELATÓRIO PARA FORNECEDOR (Inglês — Paged.js) -->
    <RelatorioPreviewModal
      :templates="templatesRelatorioFornecedorIngles"
      :dados-relatorio="dadosRelatorioFornecedorIngles"
      :visible="showRelatorioFornecedorInglesModal"
      :id-empresa="Number(authStore.empresaAtiva)"
      @fechar="showRelatorioFornecedorInglesModal = false"
      @show-notification="(p) => emit('show-notification', p)"
    />

    <!-- MODAL DE CONFIRMAÇÃO PARA PRÉ-PREENCHIMENTO -->
    <ConfirmationModal
      ref="prePreencherModalRef"
      title="Pré-preencher Purchase Order?"
      message="Deseja pré-preencher a Purchase Order com os dados do Contrato?<br><br>Se sim, os dados básicos (Filial, Fornecedor, Moeda) serão herdados do contrato e, ao salvar, as condições de pagamento do contrato serão automaticamente copiadas para esta Purchase Order. Os produtos deverão ser alocados posteriormente pelo controle de saldo."
      confirm-button-text="Sim, pré-preencher"
      cancel-button-text="Não, começar vazio"
      @confirmed="onPrePreencherConfirm"
      @close="() => {}"
    />

    <ConfirmationModal
      ref="confirmationModalRef"
      title="Alterações não salvas"
      message="Existem alterações no formulário que serão perdidas se você continuar sem salvar. Deseja realmente continuar?"
      confirm-button-text="Sim, descartar alterações"
      cancel-button-text="Não, voltar e salvar"
      @confirmed="onConfirmTransition"
      @close="onCloseTransition"
    />

    <!-- MODAIS DE PRICING -->
    <ConfirmationModal
      ref="modalConfirmacaoTrocaRef"
      title="Trocar Template"
      message="Ao trocar o template, os valores preenchidos no pricing atual serão perdidos. Deseja continuar?"
      @confirmed="confirmarTrocaTemplate"
    />

    <!-- MODAL DE CONDIÇÃO DE PAGAMENTO -->
    <ModalFormulario
      v-model="showModalCondicao"
      :titulo="condicaoEmEdicao?.idPurchaseOrderCondicaoPg ? 'Editar Condição de Pagamento' : 'Adicionar Condição de Pagamento'"
      tamanho="lg"
      :ocultar-confirmar="condicoesPagamentoBloqueadas"
      @confirmar="salvarCondicao"
    >
      <div v-if="condicaoEmEdicao" class="row g-3">
        <div class="col-md-3">
          <CampoInput
            id="condicaoPrazoDias"
            label="Prazo em Dias"
            v-model="condicaoEmEdicao.prazoDias"
            type="number"
            step="1"
            min="0"
            required
          />
        </div>
        <div class="col-md-3">
          <CampoSelect
            id="lstCondPgMomento"
            label="Momento"
            v-model="condicaoEmEdicao.lstCondPgMomento"
            :options="listas.LstCondPgMomento || []"
            option-value="idListaSistemaItem"
            option-label="textoLista"
            required
          />
        </div>
        <div class="col-md-6">
          <CampoBuscaLista
            id="lstReferenciaTemporal"
            label="Referência Temporal"
            v-model="condicaoEmEdicao.lstReferenciaTemporal"
            tabela="PurchaseOrderCondicaoPg"
            required
          />
        </div>
        <div class="col-md-3">
          <CampoInput
            id="condicaoPercentualDoTotal"
            label="% do Total"
            v-model="condicaoEmEdicao.percentualDoTotal"
            type="moeda"
            required
          />
        </div>
        <div class="col-12 mt-3">
          <div class="alert alert-info py-2 mb-0">
            <i class="bi bi-info-circle me-2"></i>
            <strong>Descrição:</strong> {{ gerarDescricaoCondicao(condicaoEmEdicao) }}
          </div>
        </div>
      </div>
    </ModalFormulario>

  </div>

  <!-- MODAL DE COTAÇÃO MANUAL (Fase 4.5) -->
  <ModalFormulario
    v-model="isModalCotacaoManualOpen"
    titulo="Definir Taxas de Câmbio Manualmente"
    tamanho="xs"
    textoConfirmar="Salvar Taxas"
    @confirmar="salvarCotacaoManual"
  >
    <div class="d-flex flex-column gap-2">
      <CampoInput id="modalTaxaCambioPadrao"
        :label="labelTaxaPadrao"
        type="moeda" :casasDecimais="6"
        v-model="cotacaoManualData.taxaCambioPadrao" />
      <CampoInput id="modalTaxaCambioGerencialPadrao"
        :label="labelTaxaGerencialPadrao"
        type="moeda" :casasDecimais="6"
        v-model="cotacaoManualData.taxaCambioGerencialPadrao" />
      <CampoInput id="modalTaxaCambioGerencial"
        :label="labelTaxaCambioGerencial"
        type="moeda" :casasDecimais="6"
        :model-value="cotacaoManualData.taxaCambioGerencial" disabled />
    </div>
    <div class="mt-2 text-muted small">
      <i class="bi bi-info-circle me-1"></i>
      O campo "{{ labelTaxaCambioGerencial }}" é calculado automaticamente:
      {{ labelTaxaPadrao }} &divide; {{ labelTaxaGerencialPadrao }}
    </div>
  </ModalFormulario>

  <!-- MODAIS DE CONFIRMAÇÃO PARA TRANSIÇÕES DE STATUS -->
  <ConfirmationModal
    ref="confirmCancelarRef"
    title="Cancelar Purchase Order"
    message="Tem certeza que deseja cancelar esta Purchase Order? Esta ação não pode ser desfeita."
    confirm-button-text="Sim, cancelar PO"
    cancel-button-text="Não, manter"
    @confirmed="onCancelarConfirmado"
  />
  <ConfirmationModal
    ref="confirmSolicitarAprovacaoRef"
    title="Solicitar Aprovação"
    message="Tem certeza que deseja solicitar a aprovação desta Purchase Order? Após enviada, a PO não poderá ser editada até a decisão do aprovador."
    confirm-button-text="Sim, solicitar aprovação"
    cancel-button-text="Não, revisar"
    @confirmed="onSolicitarAprovacaoConfirmado"
  />
  <ConfirmationModal
    ref="confirmEmitirRef"
    title="Emitir Purchase Order"
    message="Tem certeza que deseja emitir esta Purchase Order? Após emitida, a PO será enviada ao fornecedor e não poderá mais ser alterada."
    confirm-button-text="Sim, emitir PO"
    cancel-button-text="Não, manter"
    @confirmed="onEmitirConfirmado"
  />
  <ConfirmationModal
    ref="confirmEncerrarRef"
    title="Encerrar Purchase Order"
    message="Tem certeza que deseja encerrar esta Purchase Order? O sistema verificará o saldo dos produtos e aplicará o status adequado (Encerrada ou Encerrada Parcialmente)."
    confirm-button-text="Sim, encerrar PO"
    cancel-button-text="Não, manter"
    @confirmed="onEncerrarConfirmado"
  />
  <ConfirmationModal
    ref="confirmEncerrarParcialmenteRef"
    title="Encerrar Parcialmente"
    message="Tem certeza que deseja encerrar parcialmente esta Purchase Order? O saldo pendente será desconsiderado e a PO será encerrada com os itens já recebidos."
    confirm-button-text="Sim, encerrar parcialmente"
    cancel-button-text="Não, manter"
    @confirmed="onEncerrarConfirmado"
  />
  <ConfirmationModal
    ref="confirmRetornarPendenteRef"
    title="Retornar para Pendente"
    message="Tem certeza que deseja retornar esta Purchase Order para o status Pendente? A PO poderá ser editada novamente."
    confirm-button-text="Sim, retornar"
    cancel-button-text="Não, manter"
    @confirmed="onRetornarPendenteConfirmado"
  />

</template>

<style scoped>
.purchase-order-form-wrapper {
  max-width: 100%;
  overflow-x: hidden;
}

.grid-scroll-container {
  max-height: 200px;
  overflow-y: auto;
  overflow-x: auto;
}

/* Garantir que tabelas não estourem o layout em telas estreitas */
.admin-table {
  width: 100%;
  table-layout: fixed;
}

.admin-table th,
.admin-table td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
}

.admin-table-container {
  background-color: transparent;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.25rem;
  overflow-x: auto;
}

.admin-table td {
  border-bottom: 1px solid var(--bs-border-color);
  color: var(--bs-body-color);
  padding: 0.5rem 0.75rem;
}

.linha-selecionada td {
  background-color: var(--bs-info-bg-subtle) !important;
  color: var(--bs-info-text-emphasis) !important;
}

.linha-selecionada code {
  color: var(--bs-info-text-emphasis) !important;
}

.border-info-subtle {
  border-color: var(--bs-info-border-subtle) !important;
}

.bg-info-subtle {
  background-color: var(--bs-info-bg-subtle) !important;
}

.text-info-emphasis {
  color: var(--bs-info-text-emphasis) !important;
}

/* Cabeçalho do frame de Purchase Orders do Contrato (adaptável ao tema) */
.grid-po-card-header {
  background-color: var(--bs-info-bg-subtle);
  color: var(--bs-info-text-emphasis);
  border-bottom: 1px solid var(--bs-border-color);
}

[data-bs-theme="dark"] .grid-po-card-header {
  background-color: rgba(var(--bs-info-rgb), 0.18);
  color: var(--bs-info-text-emphasis);
}

.frame-bloqueado {
  opacity: 0.6;
  filter: grayscale(0.5);
  transition: all 0.3s ease;
}

/* Estilo para as nav-tabs dentro do card-header */
.nav-tabs .nav-link {
  border: none;
  color: var(--bs-secondary-color);
  font-weight: 500;
  padding: 0.75rem 1rem;
  min-width: 130px;
  text-align: center;
  transition: all 0.2s ease;
}

.nav-tabs .nav-link:hover {
  color: var(--bs-info-text-emphasis);
  background-color: rgba(var(--bs-info-rgb), 0.05);
}

.nav-tabs .nav-link.active {
  color: var(--bs-info-text-emphasis);
  background-color: transparent;
  border-bottom: 3px solid var(--bs-info);
  font-weight: 600;
}

/* ============================================================ */
/* FIX: Prevenir overflow do grid em telas estreitas            */
/* ============================================================ */
@media (max-width: 991.98px) {
  .grid-scroll-container {
    max-height: 150px;
    overflow-x: auto;
  }

  .admin-table {
    min-width: 600px;
  }
}

/* ============================================================ */
/* Condições de Pagamento (grid inline)                         */
/* ============================================================ */
.grid-scroll-container {
  overflow-x: auto;
}

.grid-scroll-container :deep(.admin-table) {
  margin-bottom: 0;
}

.grid-scroll-container :deep(.admin-table th) {
  background-color: var(--bs-secondary-bg);
  border-bottom: 2px solid var(--bs-border-color);
  white-space: nowrap;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--bs-secondary-color);
}

.grid-scroll-container :deep(.admin-table td) {
  padding: 0.5rem 0.75rem;
  vertical-align: middle;
}

.linha-editando {
  background-color: var(--bs-info-bg-subtle);
}

.inline-edit-cell {
  min-width: 120px;
}

.inline-edit-cell :deep(.campo-input-container) {
  margin-bottom: 0 !important;
}

.inline-edit-cell :deep(.form-control) {
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
  height: auto;
}

.acoes-wrapper {
  white-space: nowrap;
}

</style>
